import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { getApiUrl } from '../utils/api';
import { computeProgressCounts } from '../utils/executionProgress';
import './Executions.css';
import CycleCasesModal from './modals/CycleCasesModal';
import DeleteExecutionModal from './modals/DeleteExecutionModal';
import PauseExecutionModal from './modals/PauseExecutionModal';

// Limites de caracteres para Execuções
const EXECUTION_LIMITS = {
  name: 50,
  description: 1000,
};
const CARD_TITLE_LIMIT = 35;

const Executions = () => {
  const navigate = useNavigate();
  const truncateCardTitle = (str, max = CARD_TITLE_LIMIT) => {
    if (!str) return '';
    return str.length > max ? `${str.slice(0, max)}...` : str;
  };
  
  const { hasPermission, user } = useAuth();
  const tutorial = useTutorial();
  const [executions, setExecutions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedPullRequestNumber, setSelectedPullRequestNumber] = useState('');
  const [pullRequests, setPullRequests] = useState([]);
  
  // Ciclos de teste (para executar por ciclo)
  const [cycleList, setCycleList] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  // Removido aviso imediato para evitar alerta antes de carregar casos do ciclo
  const [loading, setLoading] = useState(false);
  const [startingExecutionIds, setStartingExecutionIds] = useState(() => new Set());
  const [downloadingNotebookIds, setDownloadingNotebookIds] = useState(() => new Set());
  const startingExecutionIdsRef = useRef(new Set());
  const fetchExecutionsRequestSeqRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Wizard "Nova Execução": passo atual (1 Detalhes, 2 Casos de teste, 3 Revisar e criar)
  const [createStep, setCreateStep] = useState(1);
  const [executionName, setExecutionName] = useState('');
  const [executionDescription, setExecutionDescription] = useState('');
  const [createErrors, setCreateErrors] = useState({ name: '', project_id: '', cycle_id: '' });
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [, setExecutionResults] = useState([]);
  // Modal de Casos do ciclo
  const [showCycleCasesModal, setShowCycleCasesModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExecution, setEditingExecution] = useState(null);
  const [users, setUsers] = useState([]);
  const [editExecutionName, setEditExecutionName] = useState('');
  const [editExecutionDescription, setEditExecutionDescription] = useState('');
  const [editResponsibleUser, setEditResponsibleUser] = useState('');
  // Estados de edição para projeto e ciclo
  const [editSelectedProject, setEditSelectedProject] = useState('');
  const [editSelectedCycleId, setEditSelectedCycleId] = useState('');
  const [editSelectedBranch, setEditSelectedBranch] = useState('');
  const [editSelectedPullRequestNumber, setEditSelectedPullRequestNumber] = useState('');
  const [executionHistory, setExecutionHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({ resultId: null, currentStatus: '', newStatus: '', comment: '' });
  // Estados para definições de status e overrides por projeto
  const [statusDefinitions, setStatusDefinitions] = useState([]);
  const [statusOverrides, setStatusOverrides] = useState([]);
  const [loadingStatusOptions, setLoadingStatusOptions] = useState(false);
  // Removido: exibição de caminho completo do ciclo nos detalhes
  // Estados para exclusão de execução
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingExecution, setDeletingExecution] = useState(null);

  // Estado e handlers de pausa/retomada de execução
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseTargetExecution, setPauseTargetExecution] = useState(null);
  


  // Estados para filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project_id: '',
    status: '',
    created_by: '',
    milestone_id: '',
    github_branch: '',
    search: '',
    date_from: '',
    date_to: '',
    test_case_id: '',
    linking: '',
    archived: 'active' // 'active' | 'archived' | 'all'
  });
  const [filterOptions, setFilterOptions] = useState({
    projects: [],
    users: [],
    milestones: [],
    branches: [],
    statuses: []
  });
  const [selectedRunIds, setSelectedRunIds] = useState(new Set());
  const isMilestoneContext = useMemo(() => {
    const s = String(filters?.milestone_id || '').trim();
    return s.length > 0;
  }, [filters?.milestone_id]);
  const [milestoneContextActive, setMilestoneContextActive] = useState(false);

  const activeMilestoneName = useMemo(() => {
    const id = String(filters?.milestone_id || '').trim();
    if (!id) return '';
    const found = (filterOptions?.milestones || []).find(m => String(m.id) === id);
    return found?.name || '';
  }, [filters?.milestone_id, filterOptions?.milestones]);

  const { showSuccess, showError } = useNotification();
  const canEditCycleCasesByRole = useMemo(() => {
    const roleUpper = String(user?.role || '').toUpperCase();
    if (['ADM', 'ADMIN', 'QA', 'TESTER', 'TRIAL', 'UNITARIO'].includes(roleUpper)) return true;
    return hasPermission('testcycles.update', ['QA', 'ADMIN', 'ADM']);
  }, [user?.role, hasPermission]);

  const closeAllModals = () => {
    try {
      setShowCycleCasesModal(false);
      setShowDetailsModal(false);
      setShowStatusModal(false);
      setShowPauseModal(false);
      setShowEditModal(false);
      setShowHistoryModal(false);
      setShowCreateModal(false);
      setShowDeleteModal(false);
    } catch (_) {}
  };

  // Normalizar role para decisão de visibilidade
  const normalizeRole = (r) => {
    const up = String(r || '').toUpperCase();
    if (up === 'ADMIN' || up === 'ADM') return 'ADM';
    if (up === 'MANAGER' || up === 'GERENCIAL') return 'GERENCIAL';
    if (up === 'TESTER' || up === 'QA') return 'QA';
    if (up === 'DEVELOPER' || up === 'DESIGNER' || up === 'DEV') return 'DEV';
    return up;
  };
  const normalizedRole = normalizeRole(user?.role);
  const isRestrictedQA = normalizedRole === 'QA';
  const isEditReadOnly = normalizedRole === 'DEV' || normalizedRole === 'GERENCIAL';

  const fetchMilestones = useCallback(async (projectId) => {
    if (!projectId) {
      setMilestones([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`milestones?project_id=${projectId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          let list = Array.isArray(result.data) ? result.data : [];
          if (normalizedRole === 'QA') {
            const uid = String(user?.id || '');
            list = list.filter(m => String(m.responsible_user_id || '') === uid || String(m.created_by || '') === uid);
          }
          setMilestones(list);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar milestones:', error);
      setMilestones([]);
    }
  }, [user?.id, normalizedRole]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('executions/filter-options'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setFilterOptions({
            projects: result.data.projects || [],
            users: result.data.users || [],
            milestones: (() => {
              const ms = result.data.milestones || [];
              if (normalizedRole === 'QA') {
                const uid = String(user?.id || '');
                return ms.filter(m => String(m.responsible_user_id || '') === uid || String(m.created_by || '') === uid);
              }
              return ms;
            })(),
            branches: result.data.branches || [],
            statuses: result.data.statuses || []
          });
        }
      } else {
        console.error('Erro na resposta do servidor:', response.status);
        setFilterOptions({ projects: [], users: [], milestones: [], branches: [], statuses: [] });
      }
    } catch (error) {
      console.error('Erro ao carregar opções de filtro:', error);
      setFilterOptions({ projects: [], users: [], milestones: [], branches: [], statuses: [] });
    }
  }, [user?.id, normalizedRole]);

  // Permissão para criar execuções
  const canCreateExecution = useMemo(() => {
    // Fallback por role global: permitir ADM/ADMIN e QA
    // As chaves 'LEADER'/'TESTER' são papéis de projeto e não se aplicam aqui
    return hasPermission('executions.create', ['ADMIN', 'ADM', 'QA', 'GERENCIAL']) && !isMilestoneContext;
  }, [hasPermission, isMilestoneContext]);

  // Permissão para excluir execuções
  const canDeleteExecution = useMemo(() => {
    return hasPermission('executions.delete', ['ADMIN', 'ADM', 'QA']);
  }, [hasPermission]);

  // Permissão para atualizar execuções (pausar/retomar)
  const canUpdateExecution = useMemo(() => {
    return hasPermission('executions.update', ['ADMIN', 'ADM', 'QA', 'GERENCIAL', 'TRIAL']);
  }, [hasPermission]);

  const canExportReports = useMemo(() => {
    return hasPermission('reports.export', ['ADMIN', 'ADM', 'QA', 'GERENCIAL']);
  }, [hasPermission]);

  const resetCreateModalFields = () => {
    setExecutionName('');
    setExecutionDescription('');
    setSelectedProject('');
    setSelectedCycleId('');
    setSelectedMilestone('');
    setSelectedBranch('');
    setSelectedPullRequestNumber('');
    setSelectedTestCases([]);
    setTestCases([]);
    setCycleList([]);
    setMilestones([]);
    setBranches([]);
    setPullRequests([]);
    setCreateErrors({ name: '', project_id: '', cycle_id: '' });
    setCreateStep(1);
  };

  const handleOpenCreateModal = () => {
    if (!canCreateExecution) {
      showError(isMilestoneContext ? 'Criação de execução desabilitada na tela de vinculação.' : 'Você não tem permissão para criar execuções.');
      return;
    }
    resetCreateModalFields();
    setShowCreateModal(true);
  };

  const handleCancelCreateModal = () => {
    resetCreateModalFields();
    closeAllModals();
  };

  // Avança o wizard "Nova Execução", validando o passo atual antes de seguir
  const handleWizardNext = () => {
    if (createStep === 1) {
      const newErrors = { name: '', project_id: '', cycle_id: '' };
      if (!executionName.trim()) newErrors.name = 'Nome da execução é obrigatório';
      if (!selectedProject) newErrors.project_id = 'Projeto é obrigatório';
      if (!selectedCycleId) newErrors.cycle_id = 'Ciclo é obrigatório';
      const hasErrors = Object.values(newErrors).some(v => v);
      setCreateErrors(newErrors);
      if (hasErrors) {
        showError('Preencha os campos obrigatórios');
        return;
      }
      setCreateStep(2);
      return;
    }
    if (createStep === 2) {
      if (!Array.isArray(testCases) || testCases.length === 0) {
        showError('O ciclo selecionado não tem casos de teste. Adicione casos ao ciclo antes de continuar.');
        return;
      }
      setCreateStep(3);
    }
  };

  const fetchExecutions = useCallback(async (appliedFilters = {}) => {
    const requestSeq = ++fetchExecutionsRequestSeqRef.current;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Para QA restrito, não força created_by; backend já aplica visibilidade (criador OU responsável)
      const effectiveFilters = { ...appliedFilters };
      
      // Construir query string com filtros (robusto para valores não-string)
      const queryParams = new URLSearchParams();
      Object.entries(effectiveFilters).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const str = typeof value === 'string' ? value : String(value);
        if (str.trim() !== '') {
          queryParams.append(key, str);
        }
      });
      
      const queryString = queryParams.toString();
      const url = getApiUrl(`executions/runs${queryString ? `?${queryString}` : ''}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (requestSeq !== fetchExecutionsRequestSeqRef.current) return;
        if (result.success) {
          const normalized = Array.isArray(result.data)
            ? result.data.map(run => ({
                ...run,
                status: normalizeStatus(run.status)
              }))
            : [];
          if (tutorial?.isTutorialMode) {
            const simulatedList = tutorial?.getSimulatedExecutions?.(appliedFilters?.project_id) || [];
            const byId = new Map();
            [...(Array.isArray(simulatedList) ? simulatedList : []), ...normalized].forEach((run) => {
              if (!run) return;
              byId.set(String(run.id), run);
            });
            setExecutions(Array.from(byId.values()));
          } else {
            setExecutions(normalized);
          }
        } else {
          showError(result.error || 'Erro ao carregar execuções');
        }
      } else {
        if (requestSeq !== fetchExecutionsRequestSeqRef.current) return;
        if (tutorial?.isTutorialMode) {
          const simulatedList = tutorial?.getSimulatedExecutions?.(appliedFilters?.project_id) || [];
          setExecutions(Array.isArray(simulatedList) ? simulatedList : []);
        }
        showError('Erro ao carregar execuções');
      }
    } catch (error) {
      if (requestSeq !== fetchExecutionsRequestSeqRef.current) return;
      console.error('Erro ao carregar execuções:', error);
      if (tutorial?.isTutorialMode) {
        const simulatedList = tutorial?.getSimulatedExecutions?.(appliedFilters?.project_id) || [];
        setExecutions(Array.isArray(simulatedList) ? simulatedList : []);
      }
      showError('Erro ao carregar execuções');
    } finally {
      if (requestSeq === fetchExecutionsRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [showError, tutorial]);

  useEffect(() => {
    // Construir filtros iniciais a partir da URL e do localStorage antes da primeira busca
    const urlParams = new URLSearchParams(window.location.search);
    // Não herdar filtros do localStorage automaticamente para evitar esconder execuções
    const initialSearch = (urlParams.has('test_case_id') && !urlParams.has('search'))
      ? ''
      : (urlParams.get('search') || '');
    const initialFilters = {
      project_id: urlParams.get('project_id') || '',
      status: urlParams.get('status') || '',
      created_by: urlParams.get('created_by') || '',
      milestone_id: urlParams.get('milestone_id') || '',
      linking: urlParams.get('linking') || '',
      search: initialSearch,
      date_from: urlParams.get('date_from') || '',
      date_to: urlParams.get('date_to') || '',
      // Evitar herdar filtro de caso salvo em localStorage para não ocultar lista por engano
      test_case_id: urlParams.get('test_case_id') || '',
      // Só 'active'/'archived' são selecionáveis (não existe visão combinada) —
      // qualquer outro valor vindo da URL (ex.: link antigo com ?archived=all) cai no padrão.
      archived: urlParams.get('archived') === 'archived' ? 'archived' : 'active'
    };

    if (initialFilters.milestone_id && !initialFilters.status && !String(initialFilters.linking || '').trim()) {
      initialFilters.status = 'planned';
    }

    setFilters(initialFilters);
    // Ajustar seleção de milestone no UI, se existir param
    if (initialFilters.milestone_id) {
      try { setSelectedMilestone(String(initialFilters.milestone_id)); } catch {}
      setMilestoneContextActive(true);
    }
    if (initialFilters.project_id) {
      try { setSelectedProject(String(initialFilters.project_id)); } catch {}
      fetchTestCases(initialFilters.project_id);
      fetchMilestones(initialFilters.project_id);
      fetchBranches(initialFilters.project_id);
      fetchCycles(initialFilters.project_id);
    }
    fetchProjects();
    fetchFilterOptions();
    fetchStatusDefinitions();
    const serverFilters = { ...initialFilters };
    if (serverFilters.milestone_id) {
      delete serverFilters.milestone_id;
    }
    fetchExecutions(serverFilters);
  }, [fetchExecutions, fetchFilterOptions, fetchMilestones]);

  // Aplicar filtros automaticamente quando mudarem (com debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const serverFilters = { ...filters };
      if (serverFilters.milestone_id && milestoneContextActive) {
        delete serverFilters.milestone_id;
      }
      fetchExecutions(serverFilters);
      localStorage.setItem('executionFilters', JSON.stringify(filters));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters, milestoneContextActive, fetchExecutions]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('projects'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setProjects(result.projects);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  // Buscar ciclos de teste por projeto
  const fetchCycles = async (projectId) => {
    try {
      if (!projectId) { setCycleList([]); return; }
      const token = localStorage.getItem('token');
      const url = getApiUrl(`test-repository/test-cycles?project_id=${projectId}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        if (tutorial?.isTutorialMode) {
          const simulatedList = tutorial?.getSimulatedTestCycles?.(projectId) || [];
          const byId = new Map();
          [...(Array.isArray(simulatedList) ? simulatedList : []), ...(Array.isArray(list) ? list : [])].forEach((c) => {
            if (!c) return;
            byId.set(String(c.id), c);
          });
          setCycleList(Array.from(byId.values()));
        } else {
          setCycleList(list);
        }
      } else {
        console.warn('Falha ao carregar ciclos');
        setCycleList([]);
      }
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
      setCycleList([]);
    }
  };

  // Removido: helper de montagem de caminho do ciclo (não exibido mais)

  

  const fetchTestCases = async (projectId) => {
    try {
      if (tutorial?.isTutorialMode) {
        const simulatedList = tutorial?.getSimulatedTestCases?.(projectId) || [];
        setTestCases(Array.isArray(simulatedList) ? simulatedList : []);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`testcases?project_id=${projectId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTestCases(result.testCases);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar casos de teste:', error);
    }
  };

  

  // Buscar casos vinculados a um ciclo
  const fetchTestCasesByCycle = async (cycleId) => {
    try {
      if (!cycleId) return;

      if (tutorial?.isTutorialMode) {
        const simulatedList = tutorial?.getSimulatedCycleCases?.(cycleId) || [];
        const list = Array.isArray(simulatedList) ? simulatedList : [];
        setTestCases(list);
        setSelectedTestCases(list.map(tc => tc.id));
        return;
      }

      const token = localStorage.getItem('token');
      const url = getApiUrl(`test-repository/test-cycles/${cycleId}/cases`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setTestCases(list);
        setSelectedTestCases(list.map(tc => tc.id));
      } else {
        console.warn('Falha ao carregar casos por ciclo');
      }
    } catch (error) {
      console.error('Erro ao carregar casos por ciclo:', error);
    }
  };

  // Carregar definições de status ativas para o domínio test_result
  const fetchStatusDefinitions = async () => {
    try {
      const cacheKey = 'statusDefinitions:test_result';
      const ttlMs = 10 * 60 * 1000;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed.items) && parsed.updatedAt && (Date.now() - parsed.updatedAt) < ttlMs) {
            setStatusDefinitions(parsed.items);
            return;
          }
        } catch (_) {}
      }

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('status?domain=test_result&active=true'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const items = Array.isArray(result.data) ? result.data : [];
          setStatusDefinitions(items);
          try {
            localStorage.setItem(cacheKey, JSON.stringify({ updatedAt: Date.now(), items }));
          } catch (_) {}
        }
      }
    } catch (error) {
      console.error('Erro ao carregar definições de status:', error);
    }
  };

  useEffect(() => {
    fetchStatusDefinitions();
  }, []);

  // Carregar overrides de status por projeto (apenas domínio test_result)
  const fetchProjectStatusOverrides = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingStatusOptions(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`status-overrides/${projectId}?domain=test_result`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStatusOverrides(Array.isArray(result.data) ? result.data : []);
        }
      } else {
        console.error('Falha ao carregar overrides de status');
      }
    } catch (error) {
      console.error('Erro ao carregar overrides de status:', error);
    } finally {
      setLoadingStatusOptions(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('users?limit=200&include_inactive=true'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsers(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchUsersByIds = async (ids) => {
    const unique = Array.from(new Set((ids || []).map(x => String(x || '').trim()).filter(Boolean)));
    if (unique.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/bulk?ids=${encodeURIComponent(unique.join(','))}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (!result?.success) return;
      const incoming = Array.isArray(result.data) ? result.data : [];
      if (incoming.length === 0) return;
      setUsers(prev => {
        const current = Array.isArray(prev) ? prev : [];
        const seen = new Set(current.map(u => String(u?.id)));
        const merged = [...current];
        for (const u of incoming) {
          const id = String(u?.id);
          if (id && !seen.has(id)) {
            merged.push(u);
            seen.add(id);
          }
        }
        return merged;
      });
    } catch (_) {}
  };

  

  const fetchBranches = async (projectId) => {
    if (!projectId) {
      setBranches([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let response = await fetch(getApiUrl(`github-app/projects/${projectId}/branches`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 404) {
        response = await fetch(getApiUrl(`github/branches/${projectId}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setBranches(result.data || result.branches || []);
        } else {
          console.warn('Erro ao carregar branches:', result.error);
          setBranches([]);
        }
      } else {
        console.warn('Erro na resposta do servidor para branches:', response.status);
        setBranches([]);
      }
    } catch (error) {
      console.error('Erro ao carregar branches:', error);
      setBranches([]);
    }
  };

  const fetchPullRequests = async (projectId) => {
    if (!projectId) {
      setPullRequests([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`github-app/projects/${projectId}/pull-requests`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPullRequests(Array.isArray(result.data) ? result.data : []);
        } else {
          console.warn('Erro ao carregar pull requests:', result.error);
          setPullRequests([]);
        }
      } else {
        console.warn('Erro na resposta do servidor para pull requests:', response.status);
        setPullRequests([]);
      }
    } catch (error) {
      console.error('Erro ao carregar pull requests:', error);
      setPullRequests([]);
    }
  };

  

  const fetchExecutionHistory = async (executionOrId) => {
    try {
      setLoadingHistory(true);
      const executionId = typeof executionOrId === 'object' && executionOrId ? executionOrId.id : executionOrId;
      const executionStatus = typeof executionOrId === 'object' && executionOrId ? executionOrId.status : undefined;
      const executionCompletedAt = typeof executionOrId === 'object' && executionOrId ? (executionOrId.completed_at || executionOrId.completedAt || executionOrId.updated_at || executionOrId.updatedAt) : undefined;
      const token = localStorage.getItem('token');
      const url = getApiUrl(`executions/runs/${executionId}/history?t=${Date.now()}`);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const list = Array.isArray(result.data)
            ? result.data
            : (Array.isArray(result?.data?.data) ? result.data.data : []);

          const statusNorm = String(executionStatus || '').trim().toLowerCase();
          const completionLikeStatuses = new Set([
            'completed',
            'concluido',
            'concluida',
            'concluída',
            'finalizado',
            'finalizada',
            'finished'
          ]);
          const completionTypes = new Set(['completed', 'autocompleted', 'auto_completed']);
          const hasCompletionEvent = list.some(h => completionTypes.has(String(h?.action_type || '').trim().toLowerCase()));
          const shouldShowCompletion = completionLikeStatuses.has(statusNorm);

          if (shouldShowCompletion && !hasCompletionEvent) {
            const synthetic = {
              id: `synthetic-completed-${executionId}`,
              test_run_id: executionId,
              user_id: null,
              action_type: 'autocompleted',
              description: 'Execução concluída',
              metadata: { source: 'client_fallback' },
              user_name: 'Sistema',
              user_email: '',
              created_at: executionCompletedAt || new Date().toISOString()
            };
            setExecutionHistory([synthetic, ...list]);
          } else {
            setExecutionHistory(list);
          }
        } else {
          showError(result.error || 'Erro ao carregar histórico');
        }
      } else {
        showError('Erro ao carregar histórico');
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      showError('Erro ao carregar histórico');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Funções para gerenciar filtros
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    const cleared = {
      project_id: '',
      status: '',
      created_by: '',
      search: '',
      date_from: '',
      date_to: '',
      test_case_id: '',
      archived: 'active'
    };
    setFilters(cleared);
    localStorage.setItem('executionFilters', JSON.stringify(cleared));
    fetchExecutions(cleared);
  };

  // Verificar se há filtros ativos
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value && value.trim() !== '');
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    setSelectedTestCases([]);
    setSelectedMilestone('');
    setSelectedBranch('');
    setSelectedPullRequestNumber('');
    setSelectedCycleId('');
    setCreateErrors(prev => ({ ...prev, project_id: '', cycle_id: '' }));
    if (projectId) {
      fetchTestCases(projectId);
      fetchMilestones(projectId);
      fetchBranches(projectId);
      fetchPullRequests(projectId);
      fetchCycles(projectId);
    } else {
      setTestCases([]);
      setMilestones([]);
      setBranches([]);
      setPullRequests([]);
      setCycleList([]);
    }
  };

  // Removido: seleção manual de casos de teste na criação de execução

  const buildPullRequestPayload = (pullRequestNumber) => {
    const value = String(pullRequestNumber || '').trim();
    if (!value) {
      return {
        github_pull_request_id: null,
        github_pull_request_number: null,
        github_pull_request_title: null,
        github_pull_request_url: null
      };
    }

    const pullRequest = (pullRequests || []).find((item) => String(item.number) === value);
    return {
      github_pull_request_id: pullRequest?.id || null,
      github_pull_request_number: pullRequest?.number ? Number(pullRequest.number) : Number(value),
      github_pull_request_title: pullRequest?.title || null,
      github_pull_request_url: pullRequest?.html_url || null
    };
  };

  const formatPullRequestLabel = (pullRequest) => {
    if (!pullRequest) return '';
    const draftPrefix = pullRequest.draft ? '[Draft] ' : '';
    return `#${pullRequest.number} - ${draftPrefix}${pullRequest.title}`;
  };

  const createExecution = async () => {
    // Checar permissão antes de criar
    if (!hasPermission('executions.create', ['ADMIN', 'ADM', 'QA', 'GERENCIAL'])) {
      showError('Você não tem permissão para criar execuções.');
      return;
    }
    {
      const newErrors = { name: '', project_id: '', cycle_id: '' };
      if (!executionName.trim()) newErrors.name = 'Nome da execução é obrigatório';
      if (!selectedProject) newErrors.project_id = 'Projeto é obrigatório';
      if (!selectedCycleId) newErrors.cycle_id = 'Ciclo é obrigatório';
      const hasErrors = Object.values(newErrors).some(v => v);
      setCreateErrors(newErrors);
      if (hasErrors) {
        showError('Preencha os campos obrigatórios');
        return;
      }
    }
    // Validar status do ciclo selecionado
    {
      const selectedCycle = (cycleList || []).find(c => String(c.id) === String(selectedCycleId));
      if (selectedCycle && String(selectedCycle.status).toLowerCase() !== 'planned') {
        showError('Ciclo selecionado não pode ser vinculado: já está em execução ou concluído.');
        return;
      }
    }
    if (Array.isArray(selectedTestCases) && selectedTestCases.length === 0) {
      showError('O ciclo selecionado não tem cenários para testes. Adicione casos ao ciclo antes de criar a execução.');
      return;
    }
    // Validações de comprimento
    if (executionName.trim().length > EXECUTION_LIMITS.name) {
      showError(`Nome da execução deve ter no máximo ${EXECUTION_LIMITS.name} caracteres`);
      return;
    }
    if ((executionDescription || '').length > EXECUTION_LIMITS.description) {
      showError(`Descrição deve ter no máximo ${EXECUTION_LIMITS.description} caracteres`);
      return;
    }

    {
      const normalizedInputName = String(executionName || '').trim().toLowerCase();
      const duplicate = (executions || []).find(r => {
        if (!r) return false;
        if (r.is_cycle) return false;
        if (String(r.project_id || '') !== String(selectedProject || '')) return false;
        const existingName = String(r.name || '').trim().toLowerCase();
        return existingName === normalizedInputName;
      });
      if (duplicate) {
        showError('Já existe uma execução com este nome neste projeto');
        return;
      }
    }

    try {
      if (tutorial?.isTutorialMode) {
        const nowIso = new Date().toISOString();
        const createdId = `tut-run-${Date.now()}`;
        const project = (projects || []).find(p => String(p?.id) === String(selectedProject)) || null;
        const cycle = (cycleList || []).find(c => String(c?.id) === String(selectedCycleId)) || null;
        const milestone = (milestones || []).find(m => String(m?.id) === String(selectedMilestone)) || null;
        const pullRequestData = buildPullRequestPayload(selectedPullRequestNumber);

        const created = {
          id: createdId,
          name: executionName,
          description: executionDescription,
          status: 'planned',
          created_at: nowIso,
          updated_at: nowIso,
          project_id: selectedProject,
          project_name: project?.name || 'Projeto',
          milestone_id: selectedMilestone || null,
          milestone_name: milestone?.name || null,
          github_branch: selectedBranch || null,
          ...pullRequestData,
          cycle_id: selectedCycleId || null,
          cycle_name: cycle?.name || cycle?.title || cycle?.cycle_name || null,
          linked_cycle_id: selectedCycleId || null,
          linked_cycle_name: cycle?.name || cycle?.title || cycle?.cycle_name || null,
          created_by: user?.id || null,
          created_by_name: user?.name || user?.username || user?.email || null,
          responsible_user_id: user?.id || null,
          responsible_user_name: user?.name || user?.username || user?.email || null,
          test_results: (Array.isArray(selectedTestCases) ? selectedTestCases : []).map((tcId) => ({
            id: `tut-res-${createdId}-${String(tcId)}`,
            test_case_id: tcId,
            status: 'planned',
          })),
        };

        tutorial?.addSimulatedExecution?.(selectedProject, created);
        setExecutions(prev => [created, ...(Array.isArray(prev) ? prev : [])]);
        showSuccess('Execução criada com sucesso');
        closeAllModals();
        resetForm();
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      const pullRequestData = buildPullRequestPayload(selectedPullRequestNumber);
      const response = await fetch(getApiUrl('executions/runs'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: executionName,
          description: executionDescription,
          project_id: selectedProject,
          milestone_id: selectedMilestone || null,
          github_branch: selectedBranch || null,
          ...pullRequestData,
          // Vincular ciclo selecionado na criação
          cycle_id: selectedCycleId || null,
          included_cases: selectedTestCases
        })
      });

      if (response.ok) {
        const result = await response.json();
        const newRunId = result?.data?.id || null;
        showSuccess('Execução criada com sucesso');
        closeAllModals();
        resetForm();
        fetchExecutions();
        // Já cair executando: a execução acabou de ser criada com os casos do ciclo prontos
        if (newRunId) {
          openExecutionByRunId(newRunId);
        }
      } else {
        const error = await response.json();
        showError(error.error || error.message || 'Erro ao criar execução');
      }
    } catch (error) {
      console.error('Erro ao criar execução:', error);
      showError('Erro ao criar execução');
    } finally {
      setLoading(false);
    }
  };

  const startExecution = async (executionOrId) => {
    const executionId = typeof executionOrId === 'object' ? executionOrId.id : executionOrId;
    const executionIdStr = String(executionId);

    if (startingExecutionIdsRef.current.has(executionIdStr)) {
      return;
    }

    startingExecutionIdsRef.current.add(executionIdStr);

    setStartingExecutionIds(prev => {
      const next = new Set(prev);
      next.add(executionIdStr);
      return next;
    });

    try {
      setLoading(true);

      if (tutorial?.isTutorialMode) {
        const nowIso = new Date().toISOString();
        const executionObj = typeof executionOrId === 'object'
          ? executionOrId
          : (Array.isArray(executions) ? executions.find(r => String(r?.id) === String(executionId)) : null);
        const projectId = executionObj?.project_id || selectedProject || filters?.project_id || '';
        tutorial?.updateSimulatedExecution?.(projectId, executionId, { status: 'running', started_at: nowIso, updated_at: nowIso });
        setExecutions(prev => (Array.isArray(prev) ? prev : []).map(run => (
          String(run?.id) === String(executionId)
            ? { ...run, status: 'running', started_at: nowIso, updated_at: nowIso }
            : run
        )));
        setSelectedExecution(prev => (
          prev && String(prev?.id) === String(executionId)
            ? { ...prev, status: 'running', started_at: nowIso, updated_at: nowIso }
            : prev
        ));
        showSuccess('Execução iniciada com sucesso');
        return;
      }

      const token = localStorage.getItem('token');
      const linkedCycleId = typeof executionOrId === 'object'
        ? (executionOrId.linked_cycle_id || executionOrId.cycle_id)
        : (selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id);

      // Se houver ciclo vinculado, validar que possui casos antes de iniciar
      if (linkedCycleId) {
        try {
          // 1. Validar casos
          const cycleResp = await fetch(getApiUrl(`test-repository/test-cycles/${linkedCycleId}/cases`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (cycleResp.ok) {
            const cycleData = await cycleResp.json();
            const casesList = Array.isArray(cycleData) ? cycleData : (Array.isArray(cycleData?.data) ? cycleData.data : []);
            if (casesList.length === 0) {
              showError('O ciclo vinculado não tem cenários para testes.');
              setLoading(false);
              return;
            }
          }
        } catch (_) { /* silenciar erros de verificação */ }
      }

      const response = await fetch(getApiUrl(`executions/runs/${executionId}/start`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSuccess('Execução iniciada com sucesso');

        fetchExecutions();

        if (linkedCycleId) {
          try {
            const runResp = await fetch(getApiUrl(`executions/runs/${linkedCycleId}`), {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (runResp.ok) {
              const runData = await runResp.json();
              const storyKey = runData?.data?.jira_story_key || runData?.jira_story_key;
              if (storyKey) {
                await fetch(getApiUrl('integrations/jira-basic/create-execution-subtasks'), {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ cycleId: linkedCycleId, storyKey })
                });
              }
            }
          } catch (e) {
            console.error('Erro ao processar integração Jira na execução:', e);
          }
        }
      } else {
        const error = await response.json();
        showError(error.error || error.message || 'Erro ao iniciar execução');
      }
    } catch (error) {
      console.error('Erro ao iniciar execução:', error);
      showError('Erro ao iniciar execução');
    } finally {
      setLoading(false);
      startingExecutionIdsRef.current.delete(executionIdStr);
      setStartingExecutionIds(prev => {
        const next = new Set(prev);
        next.delete(executionIdStr);
        return next;
      });
    }
  };

  const openDeleteModal = (execution) => {
    if (!canDeleteExecution) {
      showError('Você não tem permissão para excluir execuções.');
      return;
    }
    const s = String(execution?.status).toLowerCase();
    if (!(s === 'planned' || s === 'paused')) {
      showError('Apenas execuções planejadas ou pausadas podem ser excluídas.');
      return;
    }
    setDeletingExecution(execution);
    setShowDeleteModal(true);
  };

  const confirmDeleteExecution = async () => {
    if (!deletingExecution?.id) {
      closeAllModals();
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/runs/${deletingExecution.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        showSuccess(result.message || 'Execução excluída com sucesso');
        closeAllModals();
        setDeletingExecution(null);
        fetchExecutions();
      } else {
        showError(result.error || 'Erro ao excluir execução');
      }
    } catch (error) {
      console.error('Erro ao excluir execução:', error);
      showError('Erro ao excluir execução');
    } finally {
      setLoading(false);
    }
  };

  const openPauseModal = (execution) => {
    if (!canUpdateExecution) {
      showError('Você não tem permissão para pausar execuções.');
      return;
    }
    setPauseTargetExecution(execution);
    setShowPauseModal(true);
  };

  const closePauseModal = () => {
    closeAllModals();
    setPauseTargetExecution(null);
  };

  const pauseExecution = async (executionId, reason) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/runs/${executionId}/pause`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      const result = await response.json();
      if (response.ok) {
        showSuccess('Execução pausada com sucesso');
        setExecutions(prev => (Array.isArray(prev) ? prev : []).map(run => (
          String(run?.id) === String(executionId)
            ? { ...run, status: 'paused' }
            : run
        )));
        setSelectedExecution(prev => (
          prev && String(prev?.id) === String(executionId)
            ? { ...prev, status: 'paused' }
            : prev
        ));
        closePauseModal();
        const serverFilters = { ...filters };
        if (serverFilters.milestone_id && milestoneContextActive) {
          delete serverFilters.milestone_id;
        }
        fetchExecutions(serverFilters);
      } else {
        showError(result.error || 'Erro ao pausar execução');
      }
    } catch (error) {
      console.error('Erro ao pausar execução:', error);
      showError('Erro ao pausar execução');
    } finally {
      setLoading(false);
    }
  };

  const resumeExecution = async (executionId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/runs/${executionId}/resume`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (response.ok) {
        showSuccess('Execução retomada com sucesso');
        setExecutions(prev => (Array.isArray(prev) ? prev : []).map(run => (
          String(run?.id) === String(executionId)
            ? { ...run, status: 'running' }
            : run
        )));
        setSelectedExecution(prev => (
          prev && String(prev?.id) === String(executionId)
            ? { ...prev, status: 'running' }
            : prev
        ));
        const serverFilters = { ...filters };
        if (serverFilters.milestone_id && milestoneContextActive) {
          delete serverFilters.milestone_id;
        }
        fetchExecutions(serverFilters);
      } else {
        showError(result.error || 'Erro ao retomar execução');
      }
    } catch (error) {
      console.error('Erro ao retomar execução:', error);
      showError('Erro ao retomar execução');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (execution) => {
    setEditingExecution(execution);
    setEditExecutionName(execution.name);
    setEditExecutionDescription(execution.description || '');
    const initialResponsible = execution.responsible_user_id || execution.created_by || '';
    setEditResponsibleUser(initialResponsible);
    setEditSelectedProject(execution.project_id || '');
    setEditSelectedCycleId(execution.linked_cycle_id || execution.cycle_id || '');
    setEditSelectedBranch(execution.github_branch || '');
    setEditSelectedPullRequestNumber(execution.github_pull_request_number ? String(execution.github_pull_request_number) : '');
    
    // Abrir modal imediatamente para melhor percepção de performance
    setShowEditModal(true);
    
    // Carregar dados auxiliares em background
    fetchProjects();
    if (execution.project_id) {
      fetchCycles(execution.project_id);
      fetchBranches(execution.project_id);
      fetchPullRequests(execution.project_id);
    } else {
      setBranches([]);
      setPullRequests([]);
    }
    fetchUsers();
    if (initialResponsible) {
      fetchUsersByIds([initialResponsible]);
    }
  };

  const updateExecution = async () => {
    if (!editExecutionName.trim()) {
      showError('Nome da execução é obrigatório');
      return;
    }
    // Validações de comprimento
    if (editExecutionName.trim().length > EXECUTION_LIMITS.name) {
      showError(`Nome da execução deve ter no máximo ${EXECUTION_LIMITS.name} caracteres`);
      return;
    }
    if ((editExecutionDescription || '').length > EXECUTION_LIMITS.description) {
      showError(`Descrição deve ter no máximo ${EXECUTION_LIMITS.description} caracteres`);
      return;
    }

    {
      const normalizedInputName = String(editExecutionName || '').trim().toLowerCase();
      const finalProjectId = editSelectedProject || editingExecution?.project_id || '';
      const duplicate = (executions || []).find(r => {
        if (!r) return false;
        if (r.is_cycle) return false;
        if (String(r.id || '') === String(editingExecution?.id || '')) return false;
        if (String(r.project_id || '') !== String(finalProjectId || '')) return false;
        const existingName = String(r.name || '').trim().toLowerCase();
        return existingName === normalizedInputName;
      });
      if (duplicate) {
        showError('Já existe uma execução com este nome neste projeto');
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const pullRequestData = buildPullRequestPayload(editSelectedPullRequestNumber);
      const response = await fetch(getApiUrl(`executions/runs/${editingExecution.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editExecutionName,
          description: editExecutionDescription,
          responsible_user_id: editResponsibleUser,
          github_branch: editSelectedBranch || null,
          ...pullRequestData
        })
      });

      if (response.ok) {
        showSuccess('Execução atualizada com sucesso');
        setShowEditModal(false);
        // Recarregar lista e detalhes (se o modal de detalhes estiver aberto)
        fetchExecutions();
        try {
          if (showDetailsModal && editingExecution?.id) {
            await viewExecutionDetails({ id: editingExecution.id });
          }
        } catch (_) {}
      } else {
        const error = await response.json();
        showError(error.error || 'Erro ao atualizar execução');
      }
    } catch (error) {
      console.error('Erro ao atualizar execução:', error);
      showError('Erro ao atualizar execução');
    } finally {
      setLoading(false);
    }
  };

  const viewExecutionDetails = async (execution) => {
    // Abrir modal imediatamente com os dados que já temos
    setSelectedExecution(execution);
    setShowDetailsModal(true);

    if (tutorial?.isTutorialMode) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/runs/${execution.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Atualizar o objeto completo da execução com os dados detalhados do backend
          const detailed = result.data || {};
          setSelectedExecution(detailed);
          setExecutionResults(detailed.test_results || []);
        } else {
          showError('Erro ao carregar detalhes da execução');
        }
      } else {
        showError('Erro ao carregar detalhes da execução');
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes da execução:', error);
      showError('Erro ao carregar detalhes da execução');
    }
  };

  

  const getAvailableTransitions = (currentStatus) => {
    const transitions = {
      'planned': [
        { value: 'passed', label: 'Passou' },
        { value: 'failed', label: 'Bug' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'skipped', label: 'Ignorado' },
        { value: 'retest', label: 'Reteste' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'passed': [
        { value: 'failed', label: 'Bug' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'retest', label: 'Reteste' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'failed': [
        { value: 'passed', label: 'Passou' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'retest', label: 'Reteste' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'blocked': [
        { value: 'passed', label: 'Passou' },
        { value: 'failed', label: 'Bug' },
        { value: 'skipped', label: 'Ignorado' },
        { value: 'retest', label: 'Reteste' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'skipped': [
        { value: 'passed', label: 'Passou' },
        { value: 'failed', label: 'Bug' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'retest', label: 'Reteste' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'retest': [
        { value: 'passed', label: 'Passou' },
        { value: 'failed', label: 'Bug' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'skipped', label: 'Ignorado' },
        { value: 'improvement', label: 'Melhoria' }
      ],
      'improvement': [
        { value: 'passed', label: 'Passou' },
        { value: 'failed', label: 'Bug' },
        { value: 'blocked', label: 'Bloqueado' },
        { value: 'skipped', label: 'Ignorado' },
        { value: 'retest', label: 'Reteste' }
      ]
    };
    return transitions[currentStatus] || [];
  };

  const confirmStatusChange = async () => {
    if (!statusChangeData.newStatus) {
      showError('Selecione um novo status');
      return;
    }

    const updated = await updateTestResult(statusChangeData.resultId, statusChangeData.newStatus, statusChangeData.comment);
    if (!updated) return;
    setShowStatusModal(false);
    setStatusChangeData({ resultId: null, currentStatus: '', newStatus: '', comment: '' });
  };

  // Opções de transição filtradas por escopo (execuções) e overrides do projeto
  const getFilteredStatusOptions = (currentStatus) => {
    // Fallback: se não houver definições ainda, usar transições estáticas existentes
    if (!statusDefinitions || statusDefinitions.length === 0) {
      return getAvailableTransitions(currentStatus);
    }

    const validTransitions = {
      'planned': ['passed', 'failed', 'blocked', 'skipped', 'retest', 'improvement'],
      'passed': ['failed', 'blocked', 'improvement'],
      'failed': ['passed', 'blocked', 'improvement'],
      'blocked': ['passed', 'failed', 'skipped', 'improvement'],
      'skipped': ['passed', 'failed', 'blocked', 'improvement'],
      'retest': ['passed', 'failed', 'blocked', 'skipped'],
      'improvement': ['passed', 'failed', 'blocked', 'skipped']
    };

    const allowedKeys = validTransitions[currentStatus] || [];

    // Map de overrides por status_id
    const overridesByStatusId = {};
    statusOverrides.forEach((ov) => {
      overridesByStatusId[ov.status_id] = ov;
    });

    // Filtrar definições por escopo de execuções e por transições válidas
    const filteredDefs = statusDefinitions.filter(def => {
      const inScope = def.scope_executions === true;
      const allowsTransition = allowedKeys.includes(def.key);
      const override = overridesByStatusId[def.id];
      const notHidden = !override || override.hidden !== true;
      return inScope && allowsTransition && notHidden;
    });

    // Ordenar por override.order (se existir), senão por def.order e label
    filteredDefs.sort((a, b) => {
      const aOrder = overridesByStatusId[a.id]?.order ?? a.order ?? 0;
      const bOrder = overridesByStatusId[b.id]?.order ?? b.order ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.label || a.key).localeCompare(b.label || b.key);
    });

    return filteredDefs.map(def => ({ value: def.key, label: def.label || def.key }));
  };

  const updateTestResult = async (resultId, status, comment = '') => {
    try {
      const parentStatus = String(selectedExecution?.status || '').toLowerCase();
      if (parentStatus === 'completed' || parentStatus === 'aborted') {
        showError('Não é possível alterar o status de um caso em uma execução concluída.');
        setShowStatusModal(false);
        return false;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/results/${resultId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          comment
        })
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Status atualizado com sucesso');
        // Recarregar resultados
        const updatedResponse = await fetch(getApiUrl(`executions/runs/${selectedExecution.id}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (updatedResponse.ok) {
          const updatedResult = await updatedResponse.json();
          if (updatedResult.success) {
            setExecutionResults(updatedResult.data.test_results || []);
            if (selectedExecution && selectedExecution.milestone_id) {
              try {
                const ev = new CustomEvent('milestoneStatsUpdated', { detail: { milestoneId: selectedExecution.milestone_id } });
                window.dispatchEvent(ev);
              } catch (_) {}
            }
          }
        }
        return true;
      } else {
        showError(result.error || 'Erro ao atualizar status');
        return false;
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showError('Erro ao atualizar status');
      return false;
    }
  };

  const openHistoryModal = (execution) => {
    setSelectedExecution(execution);
    setShowHistoryModal(true);
    fetchExecutionHistory(execution);
  };

  // Arquivar/desarquivar não é exclusão e não mexe no ciclo/plano vinculado
  // — só recarrega a listagem (que por padrão já exclui arquivados).
  const handleArchiveExecution = async (execution) => {
    const isArchived = !!execution.archived_at;
    const action = isArchived ? 'unarchive' : 'archive';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`executions/runs/${execution.id}/${action}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || `Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} execução`);
        return;
      }
      showSuccess(isArchived
        ? 'Execução desarquivada com sucesso'
        : `Execução arquivada com sucesso. Após ${data?.retention_days || 90} dias sem ser desarquivada, será excluída definitivamente.`);
      fetchExecutions();
    } catch (err) {
      console.error('Erro ao arquivar/desarquivar execução:', err);
      showError(`Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} execução`);
    }
  };

  // Funções auxiliares para o histórico
  const getHistoryIcon = (actionType) => {
    switch (actionType) {
      case 'created':
        return '🆕';
      case 'responsible_changed':
        return '👤';
      case 'test_updated':
        return '✅';
      case 'started':
        return '▶️';
      case 'paused':
        return '⏸️';
      case 'resumed':
        return '▶️';
      case 'completed':
        return '🏁';
      case 'autocompleted':
        return '🏁';
      case 'auto_completed':
        return '🏁';
      case 'auto_complete_blocked':
        return '⛔';
      case 'cycle_linked':
        return '🔗';
      case 'project_changed':
        return '🗂️';
      case 'status_changed':
        return '🔄';
      case 'correction_available':
        return '🔧';
      default:
        return '📝';
    }
  };

  const getActionTypeLabel = (actionType) => {
    switch (actionType) {
      case 'created':
        return 'Execução Criada';
      case 'responsible_changed':
        return 'Responsável Alterado';
      case 'test_updated':
        return 'Teste Atualizado';
      case 'started':
        return 'Execução Iniciada';
      case 'paused':
        return 'Execução Pausada';
      case 'resumed':
        return 'Execução Retomada';
      case 'completed':
        return 'Execução Concluída';
      case 'autocompleted':
        return 'Execução Concluída (automática)';
      case 'auto_completed':
        return 'Execução Concluída (automática)';
      case 'auto_complete_blocked':
        return 'Conclusão automática bloqueada';
      case 'cycle_linked':
        return 'Ciclo Vinculado';
      case 'project_changed':
        return 'Projeto Alterado';
      case 'status_changed':
        return 'Status do ciclo atualizado';
      case 'correction_available':
        return 'Correção Recebida';
      default:
        return 'Evento';
    }
  };

  const resetForm = () => {
    setExecutionName('');
    setExecutionDescription('');
    setSelectedProject('');
    setSelectedMilestone('');
    setSelectedBranch('');
    setSelectedPullRequestNumber('');
    setSelectedCycleId('');
    setSelectedTestCases([]);
    setTestCases([]);
    setMilestones([]);
    setBranches([]);
    setPullRequests([]);
    setCycleList([]);
  };

  // Normaliza valores de status (PT/EN) para classes canônicas
  const normalizeStatus = (s) => {
    const t = String(s || '').trim().toLowerCase();
    const map = {
      // Planejado
      'planned': 'planned', 'planejado': 'planned', 'planejada': 'planned',
      // Em andamento / executando
      'running': 'running', 'active': 'running', 'executando': 'running', 'em_andamento': 'running', 'em-andamento': 'running', 'in_progress': 'running', 'resumed': 'running',
      // Pausado
      'paused': 'paused', 'pausado': 'paused', 'pausada': 'paused',
      // Concluído
      'completed': 'completed', 'concluido': 'completed', 'concluida': 'completed', 'concluída': 'completed', 'finalizado': 'completed', 'finalizada': 'completed', 'finished': 'completed',
      // Abortado
      'aborted': 'aborted', 'cancelado': 'aborted', 'cancelada': 'aborted',
      // Falhou / Bug
      'failed': 'failed', 'bug': 'failed',
      // Aprovado / Passou
      'passed': 'passed', 'aprovado': 'passed', 'aprovada': 'passed',
      // Bloqueado
      'blocked': 'blocked', 'bloqueado': 'blocked', 'bloqueada': 'blocked',
      // Ignorado
      'skipped': 'skipped', 'ignorado': 'skipped', 'ignorada': 'skipped',
      // Reteste
      'retest': 'retest',
      // Melhoria
      'improvement': 'improvement', 'melhoria': 'improvement'
    };
    return map[t] || t;
  };
  // Só permite edição quando execução está efetivamente em andamento
  const isExecutionEditable = useMemo(() => {
    const s = normalizeStatus(selectedExecution?.status);
    return s === 'running' || s === 'active';
  }, [selectedExecution?.status]);

  const [executionProgressById, setExecutionProgressById] = useState({});

  // Mesma paleta/lógica da barra "Progresso do ciclo" (CycleCasesModal): um
  // único trilho, segmentos coloridos proporcionais a cada desfecho, e o
  // percentual executado centralizado em branco sobre a barra.
  // "Em andamento" fica de fora de propósito: ele não conta como progresso
  // (só completed/bug/melhoria/bloqueado/retestar contam), então não pode
  // ser pintado — senão a barra fica 100% colorida mesmo quando o percentual
  // é menor, dando a falsa impressão de que já terminou tudo.
  const CARD_PROGRESS_SEGMENTS = [
    { key: 'completed', color: '#1C9A6C', label: 'Aprovado' },
    { key: 'bug', color: '#D6394A', label: 'Bug' },
    { key: 'improvement', color: '#B4740A', label: 'Melhoria' },
    { key: 'blocked', color: '#5B6472', label: 'Bloqueado' },
    { key: 'retest', color: '#6D4FCC', label: 'Ag. Reteste' }
  ];

  const getAlertTooltip = (execution) => {
    const p = executionProgressById[String(execution.id)] || execution.progress_summary || null;
    const bugCount = p?.bug ?? 0;
    const improvementCount = p?.improvement ?? 0;
    const retestCount = p?.retest ?? 0;
    const parts = [];
    if (bugCount > 0) parts.push(`${bugCount} bug${bugCount > 1 ? 's' : ''}`);
    if (improvementCount > 0) parts.push(`${improvementCount} melhoria${improvementCount > 1 ? 's' : ''}`);
    if (retestCount > 0) parts.push(`${retestCount} aguardando reteste`);
    return parts.length ? `Contém ${parts.join(', ')}` : 'Esta execução contém casos com bugs ou melhorias registradas.';
  };

  // Bug, bloqueio ou reteste ainda em aberto: mesmo com a execução em 100%
  // (bug/melhoria/bloqueado/reteste contam como progresso, só pendente e
  // em andamento não contam), ainda tem trabalho pendente de fato, então não
  // é um "concluído" de verdade — usado pra mostrar a tag Parcial e liberar
  // o botão Execução.
  const hasOpenIssues = (execution) => {
    const p = executionProgressById[String(execution.id)] || execution.progress_summary || null;
    return !!p && ((p.bug || 0) + (p.blocked || 0) + (p.retest || 0)) > 0;
  };

  const renderProgressBar = (execution) => {
    const p = executionProgressById[String(execution.id)] || execution.progress_summary || null;
    const total = p?.total ?? 0;
    const canonicalStatus = normalizeStatus(execution?.status);
    const percent = canonicalStatus === 'completed' ? 100 : (p?.percentCompleted ?? 0);
    const segments = CARD_PROGRESS_SEGMENTS
      .map(seg => ({ ...seg, count: p?.[seg.key] || 0, pct: total ? ((p?.[seg.key] || 0) / total) * 100 : 0 }))
      .filter(seg => seg.count > 0);
    // O texto fica centralizado na trilha — se a cor ocupada não chegar nem
    // na metade, o centro cai em cima do cinza vazio e o branco some.
    const coloredWidth = segments.reduce((sum, seg) => sum + seg.pct, 0);
    const isEmpty = coloredWidth < 50;
    return (
      <div className="execution-progress">
        <div className="execution-progress-rail">
          {segments.map(seg => (
            <div key={seg.key} title={`${seg.label}: ${seg.count}`} style={{ width: `${seg.pct}%`, background: seg.color }} />
          ))}
          <span className={`execution-progress-percent${isEmpty ? ' on-empty' : ''}`}>{percent}%</span>
        </div>
      </div>
    );
  };

  // Mesma regra usada no backend (progress_summary) e em CycleCasesModal —
  // utils/executionProgress.js. Precisa ser IDÊNTICA à fórmula do backend,
  // senão um card atualizado por aqui (refresh pontual após mudar status de
  // um caso) fica com um número diferente do resto da tela até a próxima
  // recarga da página.
  const computeProgressFromResults = (results) => computeProgressCounts(results, (r) => r.status);

  const loadProgressByIds = async (runId, executionId) => {
    const targetId = runId || executionId;
    if (!targetId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`executions/runs/${targetId}`), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      const run = data?.data || data || {};
      const counters = computeProgressFromResults(run.test_results || []);
      setExecutionProgressById(prev => ({
        ...prev,
        ...(executionId ? { [String(executionId)]: counters } : {}),
        [String(targetId)]: counters
      }));
    } catch (_) {}
  };

  const getStatusBadge = (status, execution) => {
    const statusMap = {
      'planned': { class: 'status-pending', text: 'Planejado' },
      'pending': { class: 'status-pending-exec', text: 'Pendente' },
      'running': { class: 'status-running', text: 'Executando' },
      'active': { class: 'status-running', text: 'Executando' },
      'paused': { class: 'status-paused', text: 'Pausada' },
      'completed': { class: 'status-completed', text: 'Concluído' },
      'aborted': { class: 'status-failed', text: 'Abortada' },
      'failed': { class: 'status-failed', text: 'Bug' },
      'passed': { class: 'status-passed', text: 'Passou' },
      'blocked': { class: 'status-blocked', text: 'Bloqueado' },
      'skipped': { class: 'status-pending', text: 'Ignorado' },
      'retest': { class: 'status-retest', text: 'Ag. Reteste' },
      'improvement': { class: 'status-warning', text: 'Melhoria' }
    };
    const canonical = normalizeStatus(status);
    // Uma execução pode chegar a 100% executado (bug/melhoria/bloqueado/
    // reteste todos contam como progresso) sem o status bruto ter acompanhado
    // — mesmo critério usado pra decidir em qual coluna o card aparece, pra
    // badge e coluna nunca contarem histórias diferentes.
    const p = execution ? (executionProgressById[String(execution.id)] || execution.progress_summary || null) : null;
    const isFullyExecuted = !!p && p.total > 0 && p.percentCompleted === 100;
    const effectiveCanonical = isFullyExecuted ? 'completed' : canonical;
    // "Concluído" só quando não sobrou pendência: se chegou a 100% mas ainda
    // tem bug/bloqueio/reteste em aberto, sinaliza como parcial em vez de
    // esconder que falta correção/reteste — e continua editável (ver
    // effectiveReadOnly).
    const isPartial = effectiveCanonical === 'completed' && !!execution && hasOpenIssues(execution);
    // Usar texto e classe baseadas no status canônico para evitar inconsistências visuais
    const className = isPartial ? 'status-partial' : (statusMap[effectiveCanonical]?.class || 'status-unknown');
    const baseText = isPartial ? 'Parcial' : (statusMap[effectiveCanonical]?.text || status);
    // Permitir apenas cor do repositório de status (sem alterar texto/classe)
    const def = Array.isArray(statusDefinitions)
      ? statusDefinitions.find(s => (s.domain === 'test_run' || s.domain === 'test_result') && (String(s.key || '').toLowerCase() === effectiveCanonical))
      : null;
    const color = !isPartial ? (def?.color || null) : null;
    const displayText = baseText;
    // Só força cor via estilo inline quando o admin define uma cor customizada
    // pro status — sem isso, deixa a classe CSS cuidar (paleta suave, com bom
    // contraste, que já é o padrão usado no resto do sistema).
    const style = color ? { backgroundColor: color, borderColor: color, color: '#ffffff' } : undefined;
    const title = isPartial ? 'Concluído, mas ainda tem bug ou bloqueio pendente de correção — dá pra continuar editando.' : undefined;
    return <span className={`status-badge ${className}`} style={style} title={title}>{displayText}</span>;
  };

  // Abrir direto a execução inline de um ciclo específico (ex.: logo após criá-la)
  const openExecutionByRunId = async (runId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`executions/runs/${runId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSelectedExecution(result.data);
          setShowCycleCasesModal(true);
          return;
        }
      }
      showError('Não foi possível abrir a execução deste ciclo');
    } catch (error) {
      console.error('Erro ao abrir execução do ciclo:', error);
      showError('Não foi possível abrir a execução deste ciclo');
    }
  };

  // Abrir a execução inline do ciclo (substitui o Kanban na mesma aba)
  const openCycleCasesModal = () => {
    const cycleId = selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id || null;
    if (!cycleId) {
      showError('Nenhum ciclo vinculado a esta execução');
      return;
    }
    // Fecha o modal de detalhes para revelar a execução inline por trás dele
    setShowDetailsModal(false);
    setShowCycleCasesModal(true);
  };

  // Fechar todas as modais relacionadas à tela de Execuções


  useEffect(() => {
    const handler = (e) => {
      try {
        const runId = e?.detail?.runId;
        const executionId = e?.detail?.executionId;
        if (!runId && !executionId) return;
        loadProgressByIds(runId, executionId);
        const target = executionId || runId;
        if (showDetailsModal && String(selectedExecution?.id) === String(target)) {
          viewExecutionDetails({ id: selectedExecution.id });
        }
      } catch (_) {}
    };
    window.addEventListener('cycleCaseStatusUpdated', handler);
    return () => window.removeEventListener('cycleCaseStatusUpdated', handler);
  }, [showDetailsModal, selectedExecution?.id]);
  

  // Lista de execuções visível, filtrando por contexto de milestone e test_case_id
  const visibleExecutions = useMemo(() => {
    let list = Array.isArray(executions) ? executions : [];

    // Em contexto de milestone: ocultar execuções já vinculadas a outra milestone
    if (isMilestoneContext) {
      const currentMilestoneId = String(filters?.milestone_id || '').trim();
      list = list.filter(run => {
        const mid = String(run?.milestone_id || '').trim();
        return mid === '' || (currentMilestoneId && mid === currentMilestoneId);
      });
    }

    // Filtro por caso de teste (mantém lógica existente)
    if (!filters?.test_case_id) return list;
    const targetId = String(filters.test_case_id);
    return (list || []).filter(run => {
      const includeAll =
        run?.include_all_cases === true ||
        String(run?.include_all_cases).toLowerCase() === 'true';
      if (includeAll) return true;

      const raw = run?.included_cases;
      let includedArray = [];
      try {
        if (Array.isArray(raw)) {
          includedArray = raw;
        } else if (typeof raw === 'string' && raw.length > 0) {
          const parsed = JSON.parse(raw);
          includedArray = Array.isArray(parsed) ? parsed : [];
        } else if (raw && typeof raw === 'object') {
          includedArray = Array.isArray(raw) ? raw : [];
        }
      } catch (_) {
        includedArray = [];
      }

      const includedStrs = includedArray.map(x => String(x));
      return includedStrs.includes(targetId);
    });
  }, [executions, filters?.test_case_id, filters?.milestone_id, isMilestoneContext]);

  // Progresso de cada card já vem calculado (em lote, uma única query) junto
  // com a listagem — ver `progress_summary` no backend. Isso evita disparar
  // uma requisição por card (antes deixava a tela lenta com dezenas de cards).

  // Agrupar execuções para o Kanban (Planejada, Em Andamento, Concluída)
  const kanbanData = useMemo(() => {
    const list = Array.isArray(visibleExecutions) ? visibleExecutions : [];
    // Executa por barra de progresso, não só pelo status bruto: uma execução
    // pode ter todos os casos resolvidos (100%) mas nunca ter passado pelo
    // fluxo de "Iniciar" — ficando presa em Planejada mesmo já concluída.
    const getBucket = (e) => {
      const s = normalizeStatus(e?.status);
      if (['completed', 'aborted'].includes(s)) return 'completed';
      const p = executionProgressById[String(e?.id)] || e?.progress_summary || null;
      if (p && p.total > 0 && p.percentCompleted === 100) return 'completed';
      if (['running', 'active', 'paused'].includes(s)) return 'inProgress';
      return 'planned';
    };
    const planned = list.filter(e => getBucket(e) === 'planned');
    const inProgress = list.filter(e => getBucket(e) === 'inProgress');
    const completed = list.filter(e => getBucket(e) === 'completed');
    return { planned, inProgress, completed };
  }, [visibleExecutions, executionProgressById]);

  const selectableRunIdsInMilestoneContext = useMemo(() => {
    if (!isMilestoneContext) return [];
    const all = ([]
      .concat(kanbanData.planned || [])
      .concat(kanbanData.inProgress || [])
      .concat(kanbanData.completed || []));
    return all
      .filter(e => !e.milestone_id || String(e.milestone_id).trim() === '')
      .map(e => String(e.id));
  }, [isMilestoneContext, kanbanData]);

  const toggleSelectRun = (id) => {
    const run = (visibleExecutions || []).find(e => String(e.id) === String(id));
    const selectable = run && (!run.milestone_id || String(run.milestone_id).trim() === '');
    if (isMilestoneContext && !selectable) return;
    setSelectedRunIds(prev => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAllPlanned = () => {
    setSelectedRunIds(prev => {
      const selectableIds = selectableRunIdsInMilestoneContext;
      const allSelected = selectableIds.length > 0 && selectableIds.every(id => prev.has(String(id)));
      if (allSelected) return new Set();
      return new Set(selectableIds);
    });
  };

  const linkSelectedToMilestone = async () => {
    try {
      const milestoneId = String(filters?.milestone_id || '').trim();
      if (!canUpdateExecution) {
        showError('Você não tem permissão para vincular execuções.');
        return;
      }
      if (!milestoneId) {
        showError('Selecione um milestone para vincular');
        return;
      }
      const ids = Array.from(selectedRunIds);
      const eligibleIds = ids.filter(id => {
        const run = (executions || []).find(e => String(e.id) === String(id));
        return run && (!run.milestone_id || String(run.milestone_id).trim() === '');
      });
      if (ids.length === 0) {
        showError('Nenhuma execução selecionada');
        return;
      }
      if (eligibleIds.length === 0) {
        showError('Nenhuma execução elegível para vincular');
        return;
      }
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('executions/runs/batch-link-milestone'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ milestone_id: milestoneId, run_ids: eligibleIds })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        showSuccess('Execuções vinculadas ao milestone');
        setSelectedRunIds(new Set());
        if (isMilestoneContext) {
          navigate('/milestones');
          return;
        }
        fetchExecutions(filters);
      } else {
        showError(data?.error || 'Falha ao vincular execuções');
      }
    } catch (err) {
      console.error('Erro ao vincular execuções:', err);
      showError('Erro ao vincular execuções');
    }
  };

  const downloadEvidenceNotebook = async (execution) => {
    if (!execution?.id) return;
    if (!canExportReports) {
      showError('Você não tem permissão para exportar relatórios.');
      return;
    }

    const id = String(execution.id);
    setDownloadingNotebookIds(prev => new Set(prev).add(id));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`reports/evidence-notebook/download?run_id=${encodeURIComponent(id)}&document_type=caderno`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData?.error || 'Erro ao gerar caderno de evidências');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = String(execution.name || 'Execucao').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Execucao';
      link.href = url;
      link.download = `Caderno de Evidencias - ${safeName}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Caderno de evidências gerado com sucesso.');
    } catch (err) {
      console.error('Erro ao gerar caderno de evidências:', err);
      showError('Erro ao gerar caderno de evidências');
    } finally {
      setDownloadingNotebookIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Ação de baixar o caderno de evidências/relatório — disponível mesmo com a execução
  // ainda em andamento, pra dar visibilidade do progresso diário (não só ao concluir).
  const renderNotebookAction = (execution) => {
    if (!canExportReports) return null;
    if (normalizeStatus(execution.status) === 'planned') return null;
    const isCompleted = normalizeStatus(execution.status) === 'completed';
    return (
      <div className="execution-document-action">
        <div className="document-action-icon" aria-hidden="true">📄</div>
        <div className="document-action-copy">
          <strong>{isCompleted ? 'Caderno de Evidências' : 'Relatório parcial'}</strong>
          <span>
            {isCompleted
              ? 'Documento formatado com cenários, resultados e evidências da execução concluída.'
              : 'Retrato do andamento até agora, com os casos já executados e suas evidências.'}
          </span>
        </div>
        <button
          className="btn-primary btn-sm"
          onClick={() => downloadEvidenceNotebook(execution)}
          disabled={downloadingNotebookIds.has(String(execution.id))}
        >
          {downloadingNotebookIds.has(String(execution.id)) ? 'Gerando...' : 'Baixar DOCX'}
        </button>
      </div>
    );
  };

  // Revalidação automática: se algum run estiver 'running' E for um ciclo, verifica auto-conclusão
  useEffect(() => {
    (executions || []).forEach(async (run) => {
      const s = normalizeStatus(run?.status);
      // Apenas revalidar se for um ciclo (backend rejeita execuções comuns neste endpoint)
      if (s === 'running' && run.is_cycle) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(getApiUrl(`executions/runs/${run.id}/revalidate-cycle-completion`), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (data?.auto_completed) {
            console.log('Execução autoconcluída:', run.id);
            fetchExecutions(filters);
          }
        } catch (err) {
          console.warn('Falha ao revalidar execução', run?.id, err);
        }
      }
    });
  }, [executions]);

  // Carregar casos de teste do projeto quando o filtro de projeto mudar
  useEffect(() => {
    if (filters?.project_id) {
      fetchTestCases(filters.project_id);
    }
  }, [filters?.project_id]);

  return (
    <div className="executions-container">
      <div className={`executions-header ${isMilestoneContext ? 'milestone-linking' : ''}`}>
        <div className="executions-title">
          <h1>▶️ {isMilestoneContext ? 'Vinculações de Milestones' : 'Execuções de Teste'}</h1>
          <p>Acompanhe ciclos em planejamento, execução e conclusão com seus resultados.</p>
        </div>
        <div className="header-actions">
          <button 
            className={`btn-secondary ${hasActiveFilters() ? 'filters-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '🔍 Ocultar Filtros' : '🔍 Filtros'}
            {hasActiveFilters() && <span className="filter-indicator">●</span>}
          </button>
          {canCreateExecution && (
            <button 
              className="btn-primary"
              data-tour="nova-execucao"
              onClick={handleOpenCreateModal}
              disabled={loading}
            >
              Nova Execução
            </button>
          )}
        </div>
      </div>

      {/* Componente de Filtros */}
      {showFilters && (
        <div className="filters-container">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Projeto:</label>
              <select 
                value={filters.project_id} 
                onChange={(e) => handleFilterChange('project_id', e.target.value)}
              >
                <option value="">Todos os projetos</option>
                {filterOptions.projects && filterOptions.projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos os status</option>
                {filterOptions.statuses && filterOptions.statuses.map(status => (
                  <option key={status.value || status} value={status.value || status}>
                    {status.label || status}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Arquivamento:</label>
              <select
                value={filters.archived || 'active'}
                onChange={(e) => handleFilterChange('archived', e.target.value)}
              >
                <option value="active">Ativos</option>
                <option value="archived">Arquivados</option>
              </select>
            </div>

            {!isRestrictedQA && (
              <div className="filter-group">
                <label>Criado por:</label>
                <select 
                  value={filters.created_by} 
                  onChange={(e) => handleFilterChange('created_by', e.target.value)}
                >
                  <option value="">Todos os usuários</option>
                  {filterOptions.users && filterOptions.users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Milestone e Branch GitHub removidos do filtro superior por não serem usados neste contexto */}

            <div className="filter-group">
              <label>Buscar:</label>
              <input 
                type="text"
                placeholder="Nome da execução..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Data de:</label>
              <input 
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Data até:</label>
              <input 
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </div>
          </div>

          <div className="filters-actions">
            <button className="btn-secondary" onClick={clearFilters}>
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {loading && <div className="loading">Carregando...</div>}

      {/* Removido: chip visual de "Caso filtrado" para evitar poluir layout */}

      <div className="executions-list">
        {showCycleCasesModal ? (
          <CycleCasesModal
            isOpen={showCycleCasesModal}
            onClose={(autoCompleted) => {
              closeAllModals();

              if (autoCompleted) {
                showSuccess('Execução concluída!');
                // Limpar filtro de status para evitar ocultar cartões concluídos
                setFilters(prev => ({ ...prev, status: '' }));
                // Pequeno delay para aguardar back e evitar race
                setTimeout(() => {
                  try {
                    // Recarregar execuções respeitando filtros atuais
                    fetchExecutions(filters);
                    // Forçar re-renderização do Kanban
                    setRefreshKey(prev => prev + 1);
                    loadProgressByIds(selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id, selectedExecution?.id);
                  } catch (_) {}
                }, 500);
              } else {
                // Sem autoconclusão: apenas refetch sem delay
                try {
                  fetchExecutions(filters);
                  loadProgressByIds(selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id, selectedExecution?.id);
                } catch (_) {}
              }
            }}
            cycleId={selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id || null}
            // Usar o ID do ciclo também como runId, pois os endpoints
            // de atualização de casos e revalidação esperam um ciclo (is_cycle=true)
            runId={selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id || null}
            executionId={selectedExecution?.id || null}
            // Read-only efetivo também por papel (dev/gerencial visualizam apenas)
            isReadOnly={
              tutorial?.isTutorialMode
                ? false
                : (
                    !isExecutionEditable ||
                    normalizeStatus(selectedExecution?.status) === 'completed' ||
                    !canEditCycleCasesByRole ||
                    !!selectedExecution?.archived_at
                  )
            }
          />
        ) : (
        <>
        {isMilestoneContext && (
          <div className="table-toolbar" style={{ marginBottom: 12 }}>
              <div className="table-toolbar-left">
                <label className="select-all">
                  <input type="checkbox" onChange={toggleSelectAllPlanned} checked={(() => { const selectable = selectableRunIdsInMilestoneContext; return selectable.length > 0 && selectable.every(id => selectedRunIds.has(String(id))); })()} />
                  <span>Selecionar tudo (Elegíveis)</span>
                </label>
              <span style={{ marginLeft: 8 }}>Selecionados: {selectedRunIds.size}</span>
            </div>
            <div className="table-toolbar-right">
              {activeMilestoneName && (
                <span className="chip milestone-chip" title="Milestone selecionado">
                  {activeMilestoneName}
                </span>
              )}
              <button className="btn-primary" onClick={linkSelectedToMilestone} disabled={selectedRunIds.size === 0 || !canUpdateExecution}>Vincular à milestone</button>
            </div>
          </div>
        )}
        {(visibleExecutions || []).length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma execução encontrada para os filtros aplicados</p>
            {canCreateExecution && (
              <button 
                className="btn-primary"
                onClick={handleOpenCreateModal}
              >
                Criar primeira execução
              </button>
            )}
          </div>
        ) : (
          <div key={refreshKey} className="kanban-board">
            {/* Coluna: Planejada */}
            <div className="kanban-column">
              <div className="kanban-column-header">
                <span className="kanban-column-title">Planejada</span>
                <span className="kanban-column-count">{kanbanData.planned.length}</span>
              </div>
              <div className="kanban-cards">
                {kanbanData.planned.map(execution => (
                  <div key={execution.id} className={`execution-card kanban-card ${isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') ? 'milestone-selectable-card' : ''} ${execution?.possui_alerta ? 'with-alert' : ''}`}>
                    {isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') && (
                      <div className="select-checkbox">
                        <input type="checkbox" checked={selectedRunIds.has(String(execution.id))} onChange={() => toggleSelectRun(execution.id)} />
                      </div>
                    )}
                    <div className="execution-header">
                      <h3
                        className={execution?.name && execution.name.length > CARD_TITLE_LIMIT ? 'truncated' : undefined}
                        title={execution?.name || ''}
                      >
                        {truncateCardTitle(execution.name)}
                      </h3>
                      <div className="status-with-alert">
                        {getStatusBadge(execution.status, execution)}
                        {execution?.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                        {execution?.possui_alerta && (
                          <div className="alert-icon-inline" title={getAlertTooltip(execution)}>⚠️</div>
                        )}
                      </div>
                    </div>
                    {renderProgressBar(execution)}
                    <div className="execution-meta">
                      <span>Projeto: {execution.project_name}</span>
                      <span>Criado: {new Date(execution.created_at).toLocaleDateString('pt-BR')}</span>
                      <span>Responsável: {execution.responsible_user_name || 'Não definido'}</span>
                      <span>Suíte: {execution.linked_cycle_name || execution.cycle_name || '—'}</span>
                      {execution.github_branch && <span>Branch: {execution.github_branch}</span>}
                      {execution.github_pull_request_number && (
                        <span>PR: #{execution.github_pull_request_number}</span>
                      )}
                      {isMilestoneContext && String(execution.milestone_id) === String(filters.milestone_id) ? (
                        <span style={{ marginLeft: 8 }} className="status-badge status-passed">✓ Vinculado a este milestone</span>
                      ) : (
                        execution.milestone_name ? (
                          <span style={{ marginLeft: 8 }} className="status-badge status-pending">🎯 {execution.milestone_name}</span>
                        ) : null
                      )}
                    </div>
                    <div className="execution-actions">
                      <button className="btn-secondary" data-tour="ver-detalhes" onClick={() => viewExecutionDetails(execution)}>Ver Detalhes</button>
                      { !isEditReadOnly && !execution.archived_at && (
                        <button className="btn-secondary" onClick={() => openEditModal(execution)}>Editar</button>
                      )}
                      <button className="btn-secondary" onClick={() => openHistoryModal(execution)}>Histórico</button>
                      <button className="btn-secondary" onClick={() => handleArchiveExecution(execution)}>
                        {execution.archived_at ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                      </button>
                      {execution.status === 'planned' && (
                        <>
                          { !isEditReadOnly && !execution.archived_at && (
                            <button
                              className="btn-success"
                              onClick={() => startExecution(execution)}
                              disabled={loading || startingExecutionIds.has(String(execution.id))}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                            >
                              {startingExecutionIds.has(String(execution.id)) ? (
                                <>
                                  Iniciando
                                  <span
                                    className="loading-spinner"
                                    style={{
                                      width: 14,
                                      height: 14,
                                      borderWidth: 2,
                                      borderTopWidth: 2,
                                      marginBottom: 0
                                    }}
                                  />
                                </>
                              ) : (
                                'Iniciar'
                              )}
                            </button>
                          )}
                          {canDeleteExecution && (
                            <button className="btn-danger delete-right" onClick={() => openDeleteModal(execution)} disabled={loading || startingExecutionIds.has(String(execution.id))}>🗑️ Excluir</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coluna: Em Andamento */}
            <div className="kanban-column">
              <div className="kanban-column-header">
                <span className="kanban-column-title">Em Andamento</span>
                <span className="kanban-column-count">{kanbanData.inProgress.length}</span>
              </div>
              <div className="kanban-cards">
                {kanbanData.inProgress.map(execution => (
                  <div key={execution.id} className={`execution-card kanban-card ${isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') ? 'milestone-selectable-card' : ''} ${execution?.possui_alerta ? 'with-alert' : ''} ${normalizeStatus(execution?.status) === 'paused' ? 'paused-card' : ''}`}>
                    {isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') && (
                      <div className="select-checkbox">
                        <input type="checkbox" checked={selectedRunIds.has(String(execution.id))} onChange={() => toggleSelectRun(execution.id)} />
                      </div>
                    )}
                    <div className="execution-header">
                      <h3
                        className={execution?.name && execution.name.length > CARD_TITLE_LIMIT ? 'truncated' : undefined}
                        title={execution?.name || ''}
                      >
                        {truncateCardTitle(execution.name)}
                      </h3>
                      <div className="status-with-alert">
                        {getStatusBadge(execution.status, execution)}
                        {execution?.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                        {execution?.possui_alerta && (
                          <div className="alert-icon-inline" title={getAlertTooltip(execution)}>⚠️</div>
                        )}
                      </div>
                    </div>
                    {renderProgressBar(execution)}
                    <div className="execution-meta">
                      <span>Projeto: {execution.project_name}</span>
                      <span>Criado: {new Date(execution.created_at).toLocaleDateString('pt-BR')}</span>
                      <span>Responsável: {execution.responsible_user_name || 'Não definido'}</span>
                      <span>Suíte: {execution.linked_cycle_name || execution.cycle_name || '—'}</span>
                      {execution.github_branch && <span>Branch: {execution.github_branch}</span>}
                      {execution.github_pull_request_number && (
                        <span>PR: #{execution.github_pull_request_number}</span>
                      )}
                      {isMilestoneContext && String(execution.milestone_id) === String(filters.milestone_id) ? (
                        <span style={{ marginLeft: 8 }} className="status-badge status-passed">✓ Vinculado a este milestone</span>
                      ) : (
                        execution.milestone_name ? (
                          <span style={{ marginLeft: 8 }} className="status-badge status-pending">🎯 {execution.milestone_name}</span>
                        ) : null
                      )}
                    </div>
                    {renderNotebookAction(execution)}
                    <div className="execution-actions">
                      <button className="btn-secondary" data-tour="ver-detalhes" onClick={() => viewExecutionDetails(execution)}>Ver Detalhes</button>
                      { !isEditReadOnly && !execution.archived_at && (
                        <button className="btn-secondary" onClick={() => openEditModal(execution)}>Editar</button>
                      )}
                      <button className="btn-secondary" onClick={() => openHistoryModal(execution)}>Histórico</button>
                      <button className="btn-secondary" onClick={() => handleArchiveExecution(execution)}>
                        {execution.archived_at ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                      </button>
                      {(execution.linked_cycle_id || execution.cycle_id) && (
                        <button className="btn-primary" onClick={() => { setSelectedExecution(execution); setShowCycleCasesModal(true); }}>Execução</button>
                      )}
                      {canUpdateExecution && !execution.archived_at && normalizeStatus(execution.status) === 'running' && (
                        <button className="btn-outline-warning delete-right" onClick={() => openPauseModal(execution)} title="Pausar execução">⏸️ Pausar</button>
                      )}
                      {canUpdateExecution && !execution.archived_at && normalizeStatus(execution.status) === 'paused' && (
                        <button className="btn-outline-success delete-right" onClick={() => resumeExecution(execution.id)} title="Retomar execução">▶️ Retomar</button>
                      )}
                      {canDeleteExecution && normalizeStatus(execution.status) === 'paused' && (
                        <button className="btn-danger delete-right" onClick={() => openDeleteModal(execution)} disabled={loading} title="Excluir execução">🗑️ Excluir</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coluna: Concluída */}
            <div className="kanban-column">
              <div className="kanban-column-header">
                <span className="kanban-column-title">Concluída / Parcial</span>
                <span className="kanban-column-count">{kanbanData.completed.length}</span>
              </div>
              <div className="kanban-cards">
                {kanbanData.completed.map(execution => (
                  <div key={execution.id} className={`execution-card kanban-card ${isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') ? 'milestone-selectable-card' : ''} ${execution?.possui_alerta ? 'with-alert' : ''}`}>
                    {isMilestoneContext && (!execution.milestone_id || String(execution.milestone_id).trim() === '') && (
                      <div className="select-checkbox">
                        <input type="checkbox" checked={selectedRunIds.has(String(execution.id))} onChange={() => toggleSelectRun(execution.id)} />
                      </div>
                    )}
                    <div className="execution-header">
                      <h3
                        className={execution?.name && execution.name.length > CARD_TITLE_LIMIT ? 'truncated' : undefined}
                        title={execution?.name || ''}
                      >
                        {truncateCardTitle(execution.name)}
                      </h3>
                      <div className="status-with-alert">
                        {getStatusBadge(execution.status, execution)}
                        {execution?.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                        {execution?.possui_alerta && (
                          <div className="alert-icon-inline" title={getAlertTooltip(execution)}>⚠️</div>
                        )}
                      </div>
                    </div>
                    {renderProgressBar(execution)}
                    <div className="execution-meta">
                      <span>Projeto: {execution.project_name}</span>
                      <span>Criado: {new Date(execution.created_at).toLocaleDateString('pt-BR')}</span>
                      <span>Responsável: {execution.responsible_user_name || 'Não definido'}</span>
                      <span>Suíte: {execution.linked_cycle_name || execution.cycle_name || '—'}</span>
                      {execution.github_branch && <span>Branch: {execution.github_branch}</span>}
                      {execution.github_pull_request_number && (
                        <span>PR: #{execution.github_pull_request_number}</span>
                      )}
                    </div>
                    {renderNotebookAction(execution)}
                    <div className="execution-actions">
                      <button className="btn-secondary" data-tour="ver-detalhes" onClick={() => viewExecutionDetails(execution)}>Ver Detalhes</button>
                      { !isEditReadOnly && !execution.archived_at && (
                        <button className="btn-secondary" onClick={() => openEditModal(execution)}>Editar</button>
                      )}
                      <button className="btn-secondary" onClick={() => openHistoryModal(execution)}>Histórico</button>
                      <button className="btn-secondary" onClick={() => handleArchiveExecution(execution)}>
                        {execution.archived_at ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                      </button>
                      {(execution.linked_cycle_id || execution.cycle_id) && hasOpenIssues(execution) && (
                        <button className="btn-primary" onClick={() => { setSelectedExecution(execution); setShowCycleCasesModal(true); }}>Execução</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Modal de Criação (wizard de 3 passos: Detalhes / Casos de teste / Revisar e criar) */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>Nova Execução de Teste</h2>
              <button
                className="modal-close"
                onClick={handleCancelCreateModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body" data-tour="modal-exec-campos" style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 24 }}>

              <div className="wizard-rail">
                <div className={`wizard-rail-item${createStep === 1 ? ' active' : createStep > 1 ? ' done' : ''}`}>
                  <div className="wizard-rail-num">{createStep > 1 ? '✓' : '1'}</div>
                  <div>
                    <div className="wizard-rail-title">Detalhes</div>
                    <div className="wizard-rail-sub">Nome, projeto e ciclo</div>
                  </div>
                </div>
                <div className="wizard-rail-line" />
                <div className={`wizard-rail-item${createStep === 2 ? ' active' : createStep > 2 ? ' done' : ''}`}>
                  <div className="wizard-rail-num">{createStep > 2 ? '✓' : '2'}</div>
                  <div>
                    <div className="wizard-rail-title">Casos de teste</div>
                    <div className="wizard-rail-sub">{testCases.length > 0 ? `${testCases.length} do ciclo` : 'Do ciclo selecionado'}</div>
                  </div>
                </div>
                <div className="wizard-rail-line" />
                <div className={`wizard-rail-item${createStep === 3 ? ' active' : ''}`}>
                  <div className="wizard-rail-num">3</div>
                  <div>
                    <div className="wizard-rail-title">Revisar e criar</div>
                    <div className="wizard-rail-sub">Confirme e finalize</div>
                  </div>
                </div>
              </div>

              <div>
                {createStep === 1 && (
                  <>
                    <div className="form-group">
                      <label>Nome da Execução *</label>
                      <input
                        type="text"
                        name="name"
                        value={executionName}
                        onChange={(e) => { setExecutionName(e.target.value); if (createErrors.name) setCreateErrors(prev => ({ ...prev, name: '' })); }}
                        maxLength={EXECUTION_LIMITS.name}
                        onDrop={(e) => e.preventDefault()}
                        placeholder="Digite o nome da execução"
                        className={createErrors.name ? 'error' : undefined}
                        aria-invalid={!!createErrors.name}
                      />
                      <div
                        className={(executionName?.length ?? 0) >= EXECUTION_LIMITS.name ? 'char-counter limit' : 'char-counter'}
                        aria-live="polite"
                      >
                        {(executionName?.length ?? 0)}/{EXECUTION_LIMITS.name}
                      </div>
                      {createErrors.name && (
                        <div className="field-error">{createErrors.name}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Descrição</label>
                      <textarea
                        value={executionDescription}
                        onChange={(e) => setExecutionDescription(e.target.value)}
                        maxLength={EXECUTION_LIMITS.description}
                        onDrop={(e) => e.preventDefault()}
                        placeholder="Descrição opcional"
                        rows="3"
                      />
                      <div
                        className={(executionDescription?.length ?? 0) >= EXECUTION_LIMITS.description ? 'char-counter limit' : 'char-counter'}
                        aria-live="polite"
                      >
                        {(executionDescription?.length ?? 0)}/{EXECUTION_LIMITS.description}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Projeto *</label>
                      <select
                        name="project_id"
                        value={selectedProject}
                        onChange={(e) => { handleProjectChange(e.target.value); if (createErrors.project_id) setCreateErrors(prev => ({ ...prev, project_id: '' })); }}
                        className={createErrors.project_id ? 'error' : undefined}
                        aria-invalid={!!createErrors.project_id}
                      >
                        <option value="">Selecione um projeto</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                      {createErrors.project_id && (
                        <div className="field-error">{createErrors.project_id}</div>
                      )}
                    </div>

                    {/* Campo Ciclo obrigatório */}
                    {selectedProject && (
                      <div className="form-group">
                        <label>Ciclo *</label>
                        <select
                          name="cycle_id"
                          value={selectedCycleId}
                          onChange={(e) => {
                              const cycleId = e.target.value;
                              const found = (cycleList || []).find(c => String(c.id) === String(cycleId));
                              if (found) {
                                if (String(found.status).toLowerCase() !== 'planned') {
                                  showError('Ciclo selecionado não pode ser vinculado: já está em execução ou concluído.');
                                  setSelectedCycleId('');
                                  setSelectedTestCases([]);
                                  return;
                                }
                                if (found.has_execution) {
                                  showError('Ciclo selecionado já possui uma execução vinculada.');
                                  setSelectedCycleId('');
                                  setSelectedTestCases([]);
                                  return;
                                }
                              }
                              setSelectedCycleId(cycleId);
                              if (createErrors.cycle_id) setCreateErrors(prev => ({ ...prev, cycle_id: '' }));
                              if (cycleId) {
                                fetchTestCasesByCycle(cycleId);
                              } else {
                                // Ciclo obrigatório: limpar seleção e casos
                                setSelectedTestCases([]);
                                setTestCases([]);
                              }
                            }}
                            className={createErrors.cycle_id ? 'error' : undefined}
                            aria-invalid={!!createErrors.cycle_id}
                          >
                            <option value="">Selecionar ciclo…</option>
                            {(cycleList || []).filter(cycle =>
                              String(cycle.status).toLowerCase() === 'planned' && !cycle.has_execution
                            ).map(cycle => (
                              <option key={cycle.id} value={cycle.id}>
                                {cycle.name || `Ciclo #${cycle.id}`}
                              </option>
                            ))}
                        </select>
                        {createErrors.cycle_id && (
                          <div className="field-error">{createErrors.cycle_id}</div>
                        )}
                        {selectedCycleId && (
                          <small className="form-help">
                            Casos serão associados automaticamente a partir do ciclo selecionado.
                          </small>
                        )}
                      </div>
                    )}

                    {milestones.length > 0 && (
                      <div className="form-group">
                        <label>Milestone (opcional)</label>
                        <select
                          value={selectedMilestone}
                          onChange={(e) => setSelectedMilestone(e.target.value)}
                        >
                          <option value="">Selecione um milestone</option>
                          {milestones.map(milestone => (
                            <option key={milestone.id} value={milestone.id}>
                              {milestone.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {branches.length > 0 && (
                      <div className="form-group">
                        <label>Branch GitHub (opcional)</label>
                        <select
                          value={selectedBranch}
                          onChange={(e) => setSelectedBranch(e.target.value)}
                        >
                          <option value="">Selecione uma branch</option>
                          {branches.map(branch => (
                            <option key={branch.name} value={branch.name}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {pullRequests.length > 0 && (
                      <div className="form-group">
                        <label>Pull Request GitHub (opcional)</label>
                        <select
                          value={selectedPullRequestNumber}
                          onChange={(e) => setSelectedPullRequestNumber(e.target.value)}
                        >
                          <option value="">Selecione um pull request</option>
                          {editSelectedPullRequestNumber && !(pullRequests || []).some(pr => String(pr.number) === String(editSelectedPullRequestNumber)) && (
                            <option value={editSelectedPullRequestNumber}>
                              #{editSelectedPullRequestNumber} - {editingExecution?.github_pull_request_title || 'PR vinculado'}
                            </option>
                          )}
                          {pullRequests.map(pullRequest => (
                            <option key={pullRequest.id} value={pullRequest.number}>
                              {formatPullRequestLabel(pullRequest)}
                            </option>
                          ))}
                        </select>
                        <small className="form-help">
                          PR do repositório GitHub que será validado nesta execução.
                        </small>
                      </div>
                    )}
                  </>
                )}

                {createStep === 2 && (
                  <>
                    <div style={{ marginBottom: 12, fontSize: 13, color: '#6c757d' }}>
                      Estes são os casos já vinculados ao ciclo <strong>{(cycleList || []).find(c => String(c.id) === String(selectedCycleId))?.name || ''}</strong> — eles serão incluídos automaticamente na execução.
                    </div>
                    <div className="wizard-case-preview">
                      {testCases.map(tc => (
                        <div key={tc.id} className="wizard-case-row">
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{tc.title}</div>
                            <div style={{ fontSize: 11, color: '#6c757d' }}>{tc.folder_path || tc.folder_name || 'MAIN'}</div>
                          </div>
                          {tc.priority && (
                            <span className="status-badge status-unknown" style={{ textTransform: 'capitalize' }}>{tc.priority}</span>
                          )}
                        </div>
                      ))}
                      {testCases.length === 0 && (
                        <div style={{ padding: 16, fontSize: 13, color: '#6c757d' }}>
                          Este ciclo ainda não tem casos de teste vinculados.
                        </div>
                      )}
                    </div>
                  </>
                )}

                {createStep === 3 && (
                  <div>
                    <div className="wizard-summary-row">
                      <span className="wizard-summary-label">Nome da execução</span>
                      <span className="wizard-summary-value">{executionName}</span>
                    </div>
                    <div className="wizard-summary-row">
                      <span className="wizard-summary-label">Projeto</span>
                      <span className="wizard-summary-value">{(projects || []).find(p => String(p.id) === String(selectedProject))?.name || '—'}</span>
                    </div>
                    <div className="wizard-summary-row">
                      <span className="wizard-summary-label">Ciclo</span>
                      <span className="wizard-summary-value">{(cycleList || []).find(c => String(c.id) === String(selectedCycleId))?.name || '—'}</span>
                    </div>
                    {selectedMilestone && (
                      <div className="wizard-summary-row">
                        <span className="wizard-summary-label">Milestone</span>
                        <span className="wizard-summary-value">{(milestones || []).find(m => String(m.id) === String(selectedMilestone))?.name || '—'}</span>
                      </div>
                    )}
                    {selectedBranch && (
                      <div className="wizard-summary-row">
                        <span className="wizard-summary-label">Branch</span>
                        <span className="wizard-summary-value">{selectedBranch}</span>
                      </div>
                    )}
                    <div className="wizard-summary-row">
                      <span className="wizard-summary-label">Casos de teste</span>
                      <span className="wizard-summary-value">{testCases.length} casos</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {createStep === 1 ? (
                <button className="btn-danger" onClick={handleCancelCreateModal}>Cancelar</button>
              ) : (
                <button className="btn-secondary" onClick={() => setCreateStep(createStep - 1)}>← Voltar</button>
              )}
              <span style={{ flex: 1 }} />
              {createStep < 3 ? (
                <button className="btn-primary" onClick={handleWizardNext}>Próximo →</button>
              ) : (
                <button
                  className="btn-primary"
                  data-tour="criar-execucao"
                  onClick={createExecution}
                  disabled={!selectedCycleId || loading || (Array.isArray(selectedTestCases) && selectedTestCases.length === 0)}
                  title={!selectedCycleId
                    ? 'Selecione um ciclo para criar a execução'
                    : (Array.isArray(selectedTestCases) && selectedTestCases.length === 0)
                      ? 'O ciclo selecionado não possui cenários de teste'
                      : undefined}
                >
                  Criar Execução
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Execução */}
      {showDeleteModal && (
        <DeleteExecutionModal
          isOpen={showDeleteModal}
          onClose={closeAllModals}
          onConfirm={confirmDeleteExecution}
          execution={deletingExecution}
        />
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedExecution && (
        <div className="modal-overlay">
          <div className="modal modal-compact">
            <div className="modal-header">
              <h2>{selectedExecution.name}</h2>
            </div>
            <div className="modal-body">
              {String(selectedExecution?.status) === 'completed' && (
                <div
                  className="alert"
                  style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    border: '1px solid #f59e0b',
                    borderRadius: 6,
                    background: '#fef3c7',
                    color: '#92400e',
                    fontWeight: 600
                  }}
                >
                  ⚠️ Visualização somente leitura — esta execução já foi finalizada.
                </div>
              )}
              <div className="execution-details" style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'transparent', padding: 0, marginBottom: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                  <div className="detail-item">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d', fontSize: '0.85rem' }}>Status</strong>
                    {getStatusBadge(selectedExecution.status, selectedExecution)}
                  </div>
                  <div className="detail-item">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d', fontSize: '0.85rem' }}>Projeto</strong>
                    <div style={{ fontWeight: 500 }}>{selectedExecution.project_name}</div>
                  </div>
                  <div className="detail-item">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d', fontSize: '0.85rem' }}>Criado em</strong>
                    <div style={{ fontWeight: 500 }}>{new Date(selectedExecution.created_at).toLocaleString()}</div>
                  </div>
                  <div className="detail-item">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d', fontSize: '0.85rem' }}>Branch GitHub</strong>
                    <div style={{ fontWeight: 500 }}>{selectedExecution.github_branch || 'Não vinculada'}</div>
                  </div>
                  <div className="detail-item">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d', fontSize: '0.85rem' }}>Pull Request GitHub</strong>
                    {selectedExecution.github_pull_request_number ? (
                      selectedExecution.github_pull_request_url ? (
                        <a
                          href={selectedExecution.github_pull_request_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontWeight: 500, color: '#0969da', textDecoration: 'none' }}
                        >
                          #{selectedExecution.github_pull_request_number} - {selectedExecution.github_pull_request_title || 'Abrir PR'}
                        </a>
                      ) : (
                        <div style={{ fontWeight: 500 }}>
                          #{selectedExecution.github_pull_request_number} - {selectedExecution.github_pull_request_title || 'PR vinculado'}
                        </div>
                      )
                    ) : (
                      <div style={{ fontWeight: 500 }}>Não vinculado</div>
                    )}
                  </div>
                </div>

                <div className="detail-row" style={{ margin: 0 }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '1rem' }}>Descrição</strong>
                  <div className="detail-value description-text" style={{ padding: '12px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '6px', minHeight: '60px' }}>
                    {selectedExecution?.description || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>Nenhuma descrição fornecida.</span>}
                  </div>
                </div>

                <div className="detail-row" style={{ margin: 0 }}>
                  <div className="detail-section" style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
              <div className="detail-section-header" style={{ background: '#f8f9fa', padding: '10px 16px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '1rem' }}>Ciclo de Teste Vinculado</strong>
                      <button
                        className="btn-primary btn-sm"
                        data-tour="ver-casos"
                        onClick={openCycleCasesModal}
                        disabled={!(selectedExecution?.linked_cycle_id || selectedExecution?.cycle_id)}
                        title="Abrir casos do ciclo"
                        style={{ padding: '4px 12px', fontSize: '0.85rem' }}
                      >
                        Ver Casos
                      </button>
                    </div>
                    <div className="detail-section-content" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {selectedExecution?.linked_cycle_name ? (
                        <span className="chip" style={{ fontSize: '1rem', padding: '6px 12px' }}>📦 {selectedExecution.linked_cycle_name}</span>
                      ) : selectedExecution?.cycle_name ? (
                        <span className="chip" style={{ fontSize: '1rem', padding: '6px 12px' }}>📦 {selectedExecution.cycle_name}</span>
                      ) : (
                        <span style={{ color: '#6c757d' }}>Nenhum ciclo vinculado</span>
                      )}
                    </div>
                  </div>
                </div>
                {canExportReports && normalizeStatus(selectedExecution.status) !== 'planned' && (
                  <div className="execution-document-action details-document-action">
                    <div className="document-action-icon" aria-hidden="true">📄</div>
                    <div className="document-action-copy">
                      <strong>{normalizeStatus(selectedExecution.status) === 'completed' ? 'Caderno de Evidências' : 'Relatório parcial'}</strong>
                      <span>
                        {normalizeStatus(selectedExecution.status) === 'completed'
                          ? 'Gera um DOCX no padrão do anexo com todos os cenários, resultados, bugs/melhorias e evidências desta execução.'
                          : 'Gera um DOCX com o andamento até agora — cenários já executados, resultados e evidências registradas.'}
                      </span>
                    </div>
                    <button
                      className="btn-primary btn-sm"
                      onClick={() => downloadEvidenceNotebook(selectedExecution)}
                      disabled={downloadingNotebookIds.has(String(selectedExecution.id))}
                    >
                      {downloadingNotebookIds.has(String(selectedExecution.id)) ? 'Gerando...' : 'Baixar DOCX'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-primary"
                onClick={closeAllModals}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Pausar Execução */}
      {showPauseModal && pauseTargetExecution && (
        <PauseExecutionModal
          isOpen={showPauseModal}
          execution={pauseTargetExecution}
          onClose={closePauseModal}
          onConfirm={(reason) => pauseExecution(pauseTargetExecution.id, reason)}
        />
      )}

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal modal-compact">
            <div className="modal-header">
              <h2>Editar Execução</h2>
              <button 
                className="modal-close"
                onClick={closeAllModals}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {String(editingExecution?.status) === 'completed' && (
                <div
                  className="alert"
                  style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    border: '1px solid #3b82f6',
                    borderRadius: 6,
                    background: '#eff6ff',
                    color: '#1e40af',
                    fontWeight: 600
                  }}
                >
                  🔒 Execução concluída — edição desabilitada (somente visual).
                </div>
              )}
              <div className="form-group">
                <label>Nome da Execução *</label>
                <input
                  type="text"
                  value={editExecutionName}
                  onChange={(e) => setEditExecutionName(e.target.value)}
                  maxLength={EXECUTION_LIMITS.name}
                  onDrop={(e) => e.preventDefault()}
                  placeholder="Digite o nome da execução"
                  disabled={isEditReadOnly || String(editingExecution?.status) === 'completed'}
                />
                <div
                  className={(editExecutionName?.length ?? 0) >= EXECUTION_LIMITS.name ? 'char-counter limit' : 'char-counter'}
                  aria-live="polite"
                >
                  {(editExecutionName?.length ?? 0)}/{EXECUTION_LIMITS.name}
                </div>
              </div>
              
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={editExecutionDescription}
                  onChange={(e) => setEditExecutionDescription(e.target.value)}
                  maxLength={EXECUTION_LIMITS.description}
                  onDrop={(e) => e.preventDefault()}
                  placeholder="Descrição opcional"
                  rows="3"
                  disabled={isEditReadOnly || String(editingExecution?.status) === 'completed'}
                />
                <div
                  className={(editExecutionDescription?.length ?? 0) >= EXECUTION_LIMITS.description ? 'char-counter limit' : 'char-counter'}
                  aria-live="polite"
                >
                  {(editExecutionDescription?.length ?? 0)}/{EXECUTION_LIMITS.description}
                </div>
              </div>

              <div className="form-group">
                <label>Usuário Responsável</label>
                <select
                  value={editResponsibleUser}
                  onChange={(e) => setEditResponsibleUser(e.target.value)}
                  disabled={isEditReadOnly || normalizedRole === 'UNITARIO' || String(editingExecution?.status) === 'completed'}
                >
                  <option value="">Selecione um usuário</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Selecionar Projeto */}
              <div className="form-group">
                <label>Selecionar Projeto</label>
                <select
                  value={editSelectedProject}
                  disabled
                >
                  <option value="">Selecione um projeto</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selecionar Ciclo de Teste */}
              {editSelectedProject && (
                <div className="form-group">
                  <label>Selecionar Ciclo de Teste</label>
                  <select
                    value={editSelectedCycleId}
                    disabled
                  >
                    <option value="">Nenhum ciclo vinculado</option>
                    {editSelectedCycleId && (() => {
                      const currentCycle = (cycleList || []).find(c => String(c.id) === String(editSelectedCycleId));
                      const label = currentCycle?.name || editingExecution?.linked_cycle_name || editingExecution?.cycle_name || `Ciclo #${editSelectedCycleId}`;
                      return (
                        <option key={String(editSelectedCycleId)} value={editSelectedCycleId}>
                          {label}
                        </option>
                      );
                    })()}
                  </select>
                </div>
              )}

              {branches.length > 0 && (
                <div className="form-group">
                  <label>Branch GitHub (opcional)</label>
                  <select
                    value={editSelectedBranch}
                    onChange={(e) => setEditSelectedBranch(e.target.value)}
                    disabled={isEditReadOnly || String(editingExecution?.status) === 'completed'}
                  >
                    <option value="">Selecione uma branch</option>
                    {branches.map(branch => (
                      <option key={branch.name} value={branch.name}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Branch do repositório GitHub usada como referência desta execução.
                  </small>
                </div>
              )}

              {pullRequests.length > 0 && (
                <div className="form-group">
                  <label>Pull Request GitHub (opcional)</label>
                  <select
                    value={editSelectedPullRequestNumber}
                    onChange={(e) => setEditSelectedPullRequestNumber(e.target.value)}
                    disabled={isEditReadOnly || String(editingExecution?.status) === 'completed'}
                  >
                    <option value="">Selecione um pull request</option>
                    {pullRequests.map(pullRequest => (
                      <option key={pullRequest.id} value={pullRequest.number}>
                        {formatPullRequestLabel(pullRequest)}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    PR do repositório GitHub usado como referência desta execução.
                  </small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {String(editingExecution?.status) === 'completed' || isEditReadOnly ? (
                <button 
                  className="btn-primary"
                  onClick={closeAllModals}
                >
                  Fechar
                </button>
              ) : (
                <>
                  <button 
                    className="btn-secondary"
                    onClick={closeAllModals}
                  >
                    Cancelar
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={updateExecution}
                    disabled={loading}
                  >
                    {loading ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal modal-large modal-compact">
            <div className="modal-header">
              <h2>Histórico da Execução: {selectedExecution?.name}</h2>
              <button 
                className="modal-close"
                onClick={closeAllModals}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingHistory ? (
                <div className="loading">Carregando histórico...</div>
              ) : (
                <div className="history-timeline">
                  {executionHistory.length === 0 ? (
                    <p>Nenhum histórico encontrado.</p>
                  ) : (
                    executionHistory.map((item, index) => (
                      <div key={item.id} className={`history-item ${item.action_type === 'correction_available' ? 'correction' : ''}`}>
                        <div className="history-icon">
                          {getHistoryIcon(item.action_type)}
                        </div>
                        <div className="history-content">
                          <div className="history-header">
                            <span className="history-action">
                              {getActionTypeLabel(item.action_type)}
                              {item.action_type === 'correction_available' && (
                                <span className="history-badge badge-correction" title="Bug/Melhoria corrigido — reteste recomendado">Correção recebida</span>
                              )}
                            </span>
                            <span className="history-date">
                              {new Date(item.created_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <div className="history-description">
                            {item.description}
                          </div>
                          <div className="history-user">
                            Por: {item.user_name} ({item.user_email})
                          </div>
                          {item.metadata && (
                            <div className="history-metadata">
                              <details>
                                <summary>Detalhes</summary>
                                <pre>{JSON.stringify(item.metadata, null, 2)}</pre>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={closeAllModals}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mudança de Status */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal modal-compact">
            <div className="modal-header">
              <h2>Alterar Status do Teste</h2>
              <button 
                className="modal-close"
                onClick={closeAllModals}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Aviso de execução não iniciada removido conforme solicitação */}
              <div className="form-group">
                <label>Status Atual:</label>
              <div className="current-status">
                {statusChangeData.currentStatus && (
                  <span className={`status-badge status-${statusChangeData.currentStatus}`}>
                    {statusChangeData.currentStatus === 'planned' && 'Planejado'}
                    {statusChangeData.currentStatus === 'passed' && 'Passou'}
                    {statusChangeData.currentStatus === 'failed' && 'Falhou'}
                    {statusChangeData.currentStatus === 'blocked' && 'Bloqueado'}
                    {statusChangeData.currentStatus === 'skipped' && 'Ignorado'}
                    {statusChangeData.currentStatus === 'retest' && 'Reteste'}
                  </span>
                )}
              </div>
              </div>

              <div className="form-group">
                <label>Novo Status:</label>
                <select
                  value={statusChangeData.newStatus}
                  onChange={(e) => setStatusChangeData({...statusChangeData, newStatus: e.target.value})}
                  className="form-control"
                  disabled={!isExecutionEditable}
                >
                  <option value="">Selecione um status</option>
                  {getFilteredStatusOptions(statusChangeData.currentStatus).map(transition => (
                    <option key={transition.value} value={transition.value}>
                      {transition.label}
                    </option>
                  ))}
                </select>
                <small className="form-help">
                  Opções filtradas por escopo de Execuções e overrides do projeto.
                </small>
                {loadingStatusOptions && (
                  <div className="loading-text">Carregando opções de status...</div>
                )}
              </div>

              <div className="form-group">
                <label>Justificativa (opcional):</label>
                <textarea
                  value={statusChangeData.comment}
                  onChange={(e) => setStatusChangeData({...statusChangeData, comment: e.target.value})}
                  className="form-control"
                  rows="3"
                  placeholder="Descreva o motivo da mudança de status..."
                  disabled={!isExecutionEditable}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-danger"
                onClick={closeAllModals}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary"
                onClick={confirmStatusChange}
                disabled={!statusChangeData.newStatus || !isExecutionEditable}
                title={!isExecutionEditable ? 'Disponível apenas para execuções em andamento' : undefined}
              >
                Confirmar Mudança
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Executions;
